<template>
  <v-container fluid class="pa-0 pa-md-4 py-12">

    <!-- ── Feed Guardrail Banner ─────────────────────────────────────────── -->
    <v-card
      v-if="flaggedJob"
      class="mb-4 feed-guardrail-banner"
      elevation="2"
    >
      <div class="guardrail-header pa-4 pb-3">
        <div class="d-flex align-center ga-2 mb-1">
          <v-icon color="white" size="22">mdi-shield-alert-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold text-white">
            Feed Blocked — Large Inventory Drop Detected
            ({{ flaggedJob.result?.deltaPercent ?? '?' }}% change)
          </span>
        </div>
        <div class="text-body-2 text-white" style="opacity: 0.92;">
          Website inventory is unchanged. Review the feed file before proceeding.
        </div>
      </div>

      <div class="pa-4 pt-3">
        <div class="d-flex flex-wrap ga-3 text-body-2 mb-3">
          <v-chip size="small" variant="outlined" color="orange-darken-2" prepend-icon="mdi-arrow-down-bold">
            Previous: <strong class="ml-1">{{ flaggedJob.result?.previousVinCount ?? '?' }} VINs</strong>
          </v-chip>
          <v-chip size="small" variant="outlined" color="orange-darken-2" prepend-icon="mdi-arrow-right-bold">
            New: <strong class="ml-1">{{ flaggedJob.result?.newVinCount ?? '?' }} VINs</strong>
          </v-chip>
          <v-chip size="small" variant="outlined" prepend-icon="mdi-percent">
            Threshold: {{ flaggedJob.result?.thresholdPercent ?? 50 }}%
          </v-chip>
          <v-chip size="small" variant="outlined" prepend-icon="mdi-rss">
            Feed: {{ flaggedJob.feed || '—' }}
          </v-chip>
        </div>

        <div v-if="guardrailOverrideError" class="text-caption text-error mb-3">
          {{ guardrailOverrideError }}
        </div>

        <div class="d-flex flex-wrap ga-2">
          <v-btn
            variant="outlined"
            color="grey-darken-1"
            prepend-icon="mdi-text-box-search-outline"
            :disabled="!!guardrailOverrideLoading"
            @click="viewFlaggedFeedLogs"
          >
            View Logs
          </v-btn>
          <v-btn
            variant="flat"
            color="success"
            prepend-icon="mdi-check-circle-outline"
            :loading="guardrailOverrideLoading === 'approve'"
            :disabled="guardrailOverrideLoading === 'discard'"
            @click="handleGuardrailOverride('approve')"
          >
            Approve &amp; Reprocess
          </v-btn>
          <v-btn
            variant="flat"
            color="error"
            prepend-icon="mdi-close-circle-outline"
            :loading="guardrailOverrideLoading === 'discard'"
            :disabled="guardrailOverrideLoading === 'approve'"
            @click="handleGuardrailOverride('discard')"
          >
            Discard
          </v-btn>
        </div>
      </div>
    </v-card>
    <!-- ─────────────────────────────────────────────────────────────────── -->

    <!-- Row 1: Total Vehicles / Today / Alerts -->
    <v-row align="stretch">
      <v-col cols="12" md="4">
        <v-card class="pa-4 dash-card h-100">
          <div class="d-flex align-start justify-space-between w-100">
            <div class="w-100">
              <!-- Header -->
              <div class="d-flex align-center justify-space-between">
                <div class="text-grey">Inventory</div>
                <div class="d-flex align-center ga-1" data-testid="dashboard-inventory-actions">
                  <v-btn
                    variant="text"
                    size="small"
                    color="grey"
                    class="text-caption"
                    data-testid="dashboard-inventory-inspect-action"
                    @click="goToDebug"
                  >
                    Inspect
                  </v-btn>
                  <v-btn
                    variant="text"
                    size="small"
                    color="primary"
                    class="text-caption"
                    to="/observability"
                    data-testid="dashboard-inventory-view-flow-action"
                  >
                    View Flow
                  </v-btn>
                  <v-btn
                    variant="text"
                    size="small"
                    color="primary"
                    class="text-caption"
                    prepend-icon="mdi-refresh"
                    data-testid="dashboard-republish-current-inventory"
                    :disabled="isRepublishDisabled"
                    @click="openRepublishDialog"
                  >
                    Republish
                  </v-btn>
                </div>
              </div>

              <!-- Hero: Total -->
              <div class="text-h3 font-weight-bold mt-2">
                <v-progress-circular indeterminate color="primary" size="24" v-if="stats.total == null" />
                <template v-else>{{ stats.total }}</template>
                <span class="text-body-2 text-grey ml-2">total</span>
              </div>

              <div class="text-caption mt-1 text-grey" v-if="ise.lastRun">
                as of
                <DateTimeWithDealerTooltip :value="ise.lastRunRaw" :text="ise.lastRun" />
              </div>

              <!-- Website visibility -->
              <v-sheet class="mt-4 pa-3" rounded border>
                <div class="d-flex align-center justify-space-between">
                  <div class="text-subtitle-2 font-weight-medium">Website visibility</div>

                  <!-- Percent / loader -->
                  <div class="text-caption text-grey" style="opacity: 0.7">
                    <template v-if="inventoryCountState === 'idle' || inventoryCountState === 'loading'">
                      <v-progress-circular indeterminate color="primary" size="14" />
                    </template>
                    <template v-else-if="onWebsitePct != null">
                      {{ onWebsitePct }}%
                    </template>
                    <template v-else>
                      —
                    </template>
                  </div>
                </div>

                <!-- Bar / loader -->
                <v-progress-linear
                  v-if="inventoryCountState === 'idle' || inventoryCountState === 'loading'"
                  indeterminate
                  color="primary"
                  class="mt-2"
                  height="8"
                  rounded
                />

                <!-- Segmented bar (preferred) -->
                <div
                  v-else-if="segmentsBarReady"
                  class="segbar mt-2"
                  role="img"
                  :aria-label="`On website ${onWebsiteCount ?? 0} • Hidden ${hiddenCountSafe} • Drafts ${draftCountSafe} out of ${stats.total ?? 0}`"
                >
                  <div class="seg seg-on" :style="{ width: onWebsiteBarPct + '%' }" />
                  <div class="seg seg-hidden" :style="{ width: hiddenBarPct + '%' }" />
                  <div class="seg seg-draft" :style="{ width: draftBarPct + '%' }" />
                </div>

                <!-- Fallback: single progress bar -->
                <v-progress-linear
                  v-else-if="onWebsitePct != null"
                  class="mt-2"
                  height="8"
                  rounded
                  :model-value="onWebsitePct"
                />

                <!-- Only show error when we actually failed to fetch counts -->
                <div
                  v-else-if="inventoryCountState === 'error'"
                  class="text-caption text-error mt-2"
                >
                  unable to fetch website visibility
                </div>

                <!-- Empty inventory / nothing to visualize (avoid false error when total=0) -->
                <div v-else class="text-caption text-grey mt-2">—</div>

                <!-- Metrics row (keep exactly as you had) -->
                <div class="d-flex flex-wrap ga-4 mt-3">
                  <!-- On website -->
                  <div>
                    <div class="text-caption text-grey">On website</div>
                    <div class="text-h6 font-weight-bold">
                      <template v-if="inventoryCountState === 'idle' || inventoryCountState === 'loading'">
                        <v-progress-circular indeterminate color="primary" size="14" class="ml-1" />
                      </template>
                      <template v-else-if="publishedTotal != null">
                        {{ publishedTotal }}
                      </template>
                      <template v-else>
                        <span class="text-error">unable to fetch</span>
                      </template>
                    </div>
                  </div>

                  <!-- Not on website -->
                  <div v-if="notOnWebsiteCount != null">
                    <div class="text-caption text-grey">Not on website</div>
                  <div class="text-h6 d-flex align-center">
                    <span class="font-weight-bold website-count-hidden">{{ notOnWebsiteCount }}</span>
                    <span
                      v-if="hiddenCountSafe > 0 || draftCountSafe > 0"
                      class="text-caption text-grey ml-1"
                      style="font-weight: 400"
                    >
                      (
                      <v-tooltip v-if="hiddenCountSafe > 0" text="Click to view Hidden VINs" location="top">
                        <template #activator="{ props }">
                          <span
                            v-bind="props"
                            class="clickable-metric"
                            role="button"
                            tabindex="0"
                            :aria-label="`Hidden: ${hiddenCountSafe}. Click to view VINs`"
                            @click="openWebsiteVisibilityMetric('hidden')"
                            @keydown.enter.prevent="openWebsiteVisibilityMetric('hidden')"
                            @keydown.space.prevent="openWebsiteVisibilityMetric('hidden')"
                          >
                            {{ hiddenCountSafe }} hidden
                          </span>
                        </template>
                      </v-tooltip>
                      <span v-if="hiddenCountSafe > 0 && draftCountSafe > 0" class="mx-1">•</span>
                      <v-tooltip v-if="draftCountSafe > 0" text="Click to view Draft VINs" location="top">
                        <template #activator="{ props }">
                          <span
                            v-bind="props"
                            class="clickable-metric"
                            role="button"
                            tabindex="0"
                            :aria-label="`Drafts: ${draftCountSafe}. Click to view VINs`"
                            @click="openWebsiteVisibilityMetric('drafts')"
                            @keydown.enter.prevent="openWebsiteVisibilityMetric('drafts')"
                            @keydown.space.prevent="openWebsiteVisibilityMetric('drafts')"
                          >
                            {{ draftCountSafe }} draft{{ draftCountSafe === 1 ? '' : 's' }}
                          </span>
                        </template>
                      </v-tooltip>
                      <template v-if="dealerHiddenCountDisplay != null">
                        <span class="mx-1">•</span>
                        <v-tooltip text="Click to view hidden-by-dealer VINs" location="top">
                          <template #activator="{ props }">
                            <span
                              v-bind="props"
                              class="clickable-metric"
                              role="button"
                              tabindex="0"
                              :aria-label="`Hidden by dealer: ${dealerHiddenCountDisplay}. Click to filter`"
                              @click="goToInventoryWithWebsiteVisibility('HIDDEN')"
                              @keydown.enter.prevent="goToInventoryWithWebsiteVisibility('HIDDEN')"
                              @keydown.space.prevent="goToInventoryWithWebsiteVisibility('HIDDEN')"
                            >
                              {{ dealerHiddenCountDisplay }} hidden
                            </span>
                          </template>
                        </v-tooltip>
                      </template>
                      )
                    </span>
                  </div>
                  </div>
                </div>

                <div class="text-caption text-grey mt-2" v-if="draftCountSafe > 0">
                  Drafts are newly added vehicles pending publish.
                </div>

                <div class="text-caption text-grey mt-1" v-if="reconcileHint">
                  {{ reconcileHint }}
                </div>
              </v-sheet>


              <!-- Condition breakdown (secondary) -->
              <div class="mt-4 d-flex ga-2 flex-wrap">
                <v-tooltip text="Click to view inventory" location="top">
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      size="small"
                      variant="tonal"
                      color="primary"
                      class="clickable-chip"
                      role="button"
                      tabindex="0"
                      :aria-label="`New: ${stats.new ?? 0}. Click to view inventory`"
                      @click="goToInventoryWithSegment('new')"
                      @keydown.enter.prevent="goToInventoryWithSegment('new')"
                      @keydown.space.prevent="goToInventoryWithSegment('new')"
                    >
                      New <strong class="ml-1">{{ stats.new ?? 0 }}</strong>
                    </v-chip>
                  </template>
                </v-tooltip>

                <v-tooltip text="Click to view inventory" location="top">
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      size="small"
                      variant="tonal"
                      color="warning"
                      class="clickable-chip"
                      role="button"
                      tabindex="0"
                      :aria-label="`Used: ${stats.used ?? 0}. Click to view inventory`"
                      @click="goToInventoryWithSegment('used')"
                      @keydown.enter.prevent="goToInventoryWithSegment('used')"
                      @keydown.space.prevent="goToInventoryWithSegment('used')"
                    >
                      Used <strong class="ml-1">{{ stats.used ?? 0 }}</strong>
                    </v-chip>
                  </template>
                </v-tooltip>

                <v-tooltip text="Click to view inventory" location="top">
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      size="small"
                      variant="tonal"
                      color="grey"
                      class="clickable-chip"
                      role="button"
                      tabindex="0"
                      :aria-label="`CPO: ${stats.cpo ?? 0}. Click to view inventory`"
                      @click="goToInventoryWithSegment('cpo')"
                      @keydown.enter.prevent="goToInventoryWithSegment('cpo')"
                      @keydown.space.prevent="goToInventoryWithSegment('cpo')"
                    >
                      CPO <strong class="ml-1">{{ stats.cpo ?? 0 }}</strong>
                    </v-chip>
                  </template>
                </v-tooltip>

                <template v-if="dashboardSegmentsReady">
                  <v-tooltip
                    v-for="seg in segmentSummary"
                    :key="seg.id"
                    text="Click to view inventory"
                    location="top"
                  >
                    <template #activator="{ props }">
                      <v-chip
                        v-bind="props"
                        size="small"
                        variant="tonal"
                        color="info"
                        class="clickable-chip"
                        role="button"
                        tabindex="0"
                        :aria-label="`${seg.label}: ${seg.count}. Click to view inventory`"
                        @click="goToInventoryWithCustomSegment(seg.id)"
                        @keydown.enter.prevent="goToInventoryWithCustomSegment(seg.id)"
                        @keydown.space.prevent="goToInventoryWithCustomSegment(seg.id)"
                      >
                        {{ seg.label }} <strong class="ml-1">{{ seg.count }}</strong>
                      </v-chip>
                    </template>
                  </v-tooltip>
                </template>

                <v-tooltip
                  v-if="dashboardSegmentsReady && customSegments.length > 0"
                  :text="customSegments.length > 5 ? 'View all segments' : 'Manage segments'"
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      size="small"
                      variant="outlined"
                      color="grey"
                      class="clickable-chip"
                      role="button"
                      tabindex="0"
                      :aria-label="customSegments.length > 5 ? 'More segments. Click to manage.' : 'Manage segments. Click to open segments.'"
                      @click="$router.push('/segments')"
                      @keydown.enter.prevent="$router.push('/segments')"
                      @keydown.space.prevent="$router.push('/segments')"
                    >
                      {{ customSegments.length > 5 ? 'More' : 'Manage' }}
                    </v-chip>
                  </template>
                </v-tooltip>
              </div>

              <!-- Source breakdown (tertiary) -->
              <div
                class="text-caption text-grey mt-3"
                v-if="sourceBreakdown.feed != null && sourceBreakdown.manual != null"
              >
                Source:
                <v-tooltip text="Click to view inventory" location="top">
                  <template #activator="{ props }">
                    <span
                      v-bind="props"
                      class="clickable-metric"
                      role="button"
                      tabindex="0"
                      :aria-label="`Feed: ${sourceBreakdown.feed}. Click to view inventory`"
                      @click="goToInventoryWithType('auto')"
                      @keydown.enter.prevent="goToInventoryWithType('auto')"
                      @keydown.space.prevent="goToInventoryWithType('auto')"
                    >
                      <strong>{{ sourceBreakdown.feed }}</strong> feed
                  </span>
                  </template>
                </v-tooltip>
                <span class="mx-1">•</span>
                <v-tooltip text="Click to view inventory" location="top">
                  <template #activator="{ props }">
                    <span
                      v-bind="props"
                      class="clickable-metric"
                      role="button"
                      tabindex="0"
                      :aria-label="`Manual: ${sourceBreakdown.manual}. Click to view inventory`"
                      @click="goToInventoryWithType('manual')"
                      @keydown.enter.prevent="goToInventoryWithType('manual')"
                      @keydown.space.prevent="goToInventoryWithType('manual')"
                    >
                      <strong>{{ sourceBreakdown.manual }}</strong> manual
                  </span>
                  </template>
                </v-tooltip>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="pa-4 dash-card h-100">
          <div class="d-flex justify-space-between align-center">
            <div class="text-grey">Inventory Change Today</div>
            <div class="text-caption text-grey-lighten-1" style="opacity: 0.6">
              {{ runsToday }} {{ runsToday === 1 ? 'run' : 'runs' }}
            </div>
          </div>
          <div
            class="text-h4 font-weight-bold mt-2"
            :class="netChange > 0 ? 'text-success' : netChange < 0 ? 'text-error' : 'text-grey'"
          >
            {{ netChange > 0 ? `+${netChange}` : netChange }}
          </div>
          <div class="text-caption text-grey mt-1">Vehicles gained/lost</div>
          <div class="text-body-2 text-grey mt-2">{{ todaySummary }}</div>
          <div class="d-flex ga-2 mt-3 flex-wrap">
            <v-chip
              color="green"
              variant="tonal"
              class="cursor-pointer"
              title="Click to view VINs"
              @click="openActivity('today', 'added')"
            >
              Added {{ todayAdded }}
            </v-chip>
            <v-chip
              color="red"
              variant="tonal"
              class="cursor-pointer"
              title="Click to view VINs"
              @click="openActivity('today', 'removed')"
            >
              Removed {{ todayRemoved }}
            </v-chip>
          </div>

          <div class="text-caption text-grey mt-3">
            This week:
            <strong
              class="cursor-pointer text-success"
              title="Click to view VINs"
              @click="openActivity('week', 'added')"
              >+{{ weekAdded }}</strong
            >
            /
            <strong
              class="cursor-pointer text-error"
              title="Click to view VINs"
              @click="openActivity('week', 'removed')"
              >-{{ weekRemoved }}</strong
            >
            <span class="mx-2">•</span>
            This month:
            <strong
              class="cursor-pointer text-success"
              title="Click to view VINs"
              @click="openActivity('month', 'added')"
              >+{{ monthAdded }}</strong
            >
            /
            <strong
              class="cursor-pointer text-error"
              title="Click to view VINs"
              @click="openActivity('month', 'removed')"
              >-{{ monthRemoved }}</strong
            >
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="pa-4 dash-card h-100">
          <div class="d-flex align-center justify-space-between">
            <div class="text-grey">Alerts</div>
          </div>
          <div class="mt-2">
            <div class="text-body-2 text-grey">All good — no alerts.</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col cols="12">
        <v-card class="pa-4 dash-card" elevation="0">
          <div class="d-flex flex-column flex-md-row align-md-center justify-space-between ga-3 mb-4">
            <div class="font-weight-bold">Latest Inventory Update</div>
            <div class="d-flex flex-wrap justify-end ga-2">
              <template v-if="latestInventoryUpdateState === 'loaded' && (hasProcessedData || hasPublishHistory)">
                <v-btn
                  v-if="ise.correlationId"
                  size="small"
                  variant="outlined"
                  color="primary"
                  :to="`/observability/publish/${ise.correlationId}`"
                >
                  View Flow
                </v-btn>
                <v-btn size="small" variant="outlined" @click="showHistory = true">View Update History</v-btn>
                <v-btn size="small" variant="outlined" @click="goToAccountConfiguration">
                  View Current Configuration
                </v-btn>
              </template>
            </div>
          </div>
          <div v-if="latestInventoryUpdateState === 'loading'" class="pa-8 text-center">
            <v-progress-circular indeterminate color="primary" size="40" class="mb-4" />
            <div class="text-subtitle-1 mb-1">Loading latest inventory update…</div>
            <div class="text-body-2 text-grey">
              Fetching the latest processing results and feeds used for this account.
            </div>
          </div>
          <v-alert
            v-else-if="latestInventoryUpdateState === 'error'"
            type="error"
            variant="tonal"
            density="comfortable"
          >
            {{ latestInventoryUpdateError || 'Failed to load the latest inventory update.' }}
          </v-alert>
          <v-row v-else align="stretch">
            <v-col cols="12" md="8">
              <v-sheet class="pa-4 h-100 publish-section-card" rounded>
                <v-row class="align-center">
                  <v-col>
                    <div class="font-weight-bold">What Changed</div>
                    <div v-if="ise.lastRun" class="text-body-2 text-grey">
                      Last run:
                      <DateTimeWithDealerTooltip :value="ise.lastRunRaw" :text="ise.lastRun" />
                    </div>
                  </v-col>
                </v-row>

                <template v-if="hasLatestInventoryUpdate">
                  <div class="ise-metrics-grid mt-4">
                    <div>
                      <v-tooltip v-if="iseAddedBox > 0" text="Click to view VINs" location="top">
                        <template #activator="{ props }">
                          <v-sheet
                            v-bind="props"
                            class="metric-box metric-added clickable-metric"
                            rounded
                            role="button"
                            tabindex="0"
                            :aria-label="`Added: ${iseAddedBox}. Click to view VINs`"
                            @click="openIseMetric('added')"
                            @keydown.enter.prevent="openIseMetric('added')"
                            @keydown.space.prevent="openIseMetric('added')"
                          >
                            <div class="text-subtitle-2 text-success">Added</div>
                            <div class="text-h5 font-weight-bold">{{ iseAddedBox }}</div>
                          </v-sheet>
                        </template>
                      </v-tooltip>
                      <v-sheet v-else class="metric-box metric-added" rounded>
                        <div class="text-subtitle-2 text-success">Added</div>
                        <div class="text-h5 font-weight-bold">{{ iseAddedBox }}</div>
                      </v-sheet>
                    </div>

                    <div>
                      <v-tooltip v-if="iseRemovedBox > 0" text="Click to view VINs" location="top">
                        <template #activator="{ props }">
                          <v-sheet
                            v-bind="props"
                            class="metric-box metric-removed clickable-metric"
                            rounded
                            role="button"
                            tabindex="0"
                            :aria-label="`Removed: ${iseRemovedBox}. Click to view VINs`"
                            @click="openIseMetric('removed')"
                            @keydown.enter.prevent="openIseMetric('removed')"
                            @keydown.space.prevent="openIseMetric('removed')"
                          >
                            <div class="text-subtitle-2 text-error">Removed</div>
                            <div class="text-h5 font-weight-bold">{{ iseRemovedBox }}</div>
                          </v-sheet>
                        </template>
                      </v-tooltip>
                      <v-sheet v-else class="metric-box metric-removed" rounded>
                        <div class="text-subtitle-2 text-error">Removed</div>
                        <div class="text-h5 font-weight-bold">{{ iseRemovedBox }}</div>
                      </v-sheet>
                    </div>

                    <div>
                      <v-tooltip v-if="iseUpdatedBox > 0" text="Click to view VINs" location="top">
                        <template #activator="{ props }">
                          <v-sheet
                            v-bind="props"
                            class="metric-box metric-updated clickable-metric"
                            rounded
                            role="button"
                            tabindex="0"
                            :aria-label="`Updated: ${iseUpdatedBox}. Click to view VINs`"
                            @click="openIseMetric('updated')"
                            @keydown.enter.prevent="openIseMetric('updated')"
                            @keydown.space.prevent="openIseMetric('updated')"
                          >
                            <div class="text-subtitle-2 text-info">Updated</div>
                            <div class="text-h5 font-weight-bold">{{ iseUpdatedBox }}</div>
                          </v-sheet>
                        </template>
                      </v-tooltip>
                      <v-sheet v-else class="metric-box metric-updated" rounded>
                        <div class="text-subtitle-2 text-info">Updated</div>
                        <div class="text-h5 font-weight-bold">{{ iseUpdatedBox }}</div>
                      </v-sheet>
                    </div>

                    <div>
                      <v-tooltip v-if="iseListedBox > 0" text="Click to view VINs" location="top">
                        <template #activator="{ props }">
                          <v-sheet
                            v-bind="props"
                            class="metric-box metric-updated clickable-metric"
                            rounded
                            role="button"
                            tabindex="0"
                            :aria-label="`Made visible: ${iseListedBox}. Click to view VINs`"
                            @click="openIseMetric('listed')"
                            @keydown.enter.prevent="openIseMetric('listed')"
                            @keydown.space.prevent="openIseMetric('listed')"
                          >
                            <div class="text-subtitle-2 text-success">Made Visible</div>
                            <div class="text-h5 font-weight-bold">{{ iseListedBox }}</div>
                          </v-sheet>
                        </template>
                      </v-tooltip>
                      <v-sheet v-else class="metric-box metric-updated" rounded>
                        <div class="text-subtitle-2 text-success">Made Visible</div>
                        <div class="text-h5 font-weight-bold">{{ iseListedBox }}</div>
                      </v-sheet>
                    </div>

                    <div>
                      <v-tooltip v-if="iseDelistedBox > 0" text="Click to view VINs" location="top">
                        <template #activator="{ props }">
                          <v-sheet
                            v-bind="props"
                            class="metric-box metric-updated clickable-metric"
                            rounded
                            role="button"
                            tabindex="0"
                            :aria-label="`Hidden: ${iseDelistedBox}. Click to view VINs`"
                            @click="openIseMetric('delisted')"
                            @keydown.enter.prevent="openIseMetric('delisted')"
                            @keydown.space.prevent="openIseMetric('delisted')"
                          >
                            <div class="text-subtitle-2" style="color: #b45309">Hidden</div>
                            <div class="text-h5 font-weight-bold">{{ iseDelistedBox }}</div>
                          </v-sheet>
                        </template>
                      </v-tooltip>
                      <v-sheet v-else class="metric-box metric-updated" rounded>
                        <div class="text-subtitle-2" style="color: #b45309">Hidden</div>
                        <div class="text-h5 font-weight-bold">{{ iseDelistedBox }}</div>
                      </v-sheet>
                    </div>
                  </div>

                  <v-divider class="my-4" />

                  <v-expansion-panels variant="accordion" class="mt-2">
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-caption">View details</v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div class="text-caption text-grey">
                          Triggered by
                          <span v-if="ise.correlationId"
                            >{{ ise.triggerType === 'user' ? 'user' : 'feed' }} (Correlation ID: {{ ise.correlationId }}{{ ise.triggerType === 'user' ? `, User: ${ise.triggeredBy || 'anonymous'}` : '' }})</span
                          >
                        </div>

                        <div
                          v-if="publishRunMetricsState === 'loading'"
                          class="text-body-2 text-grey mt-2"
                        >
                          Loading run metrics…
                        </div>
                        <div
                          v-else-if="publishRunMetricsState === 'error'"
                          class="text-body-2 text-error mt-2"
                        >
                          {{ publishRunMetricsError || 'Failed to load run metrics' }}
                        </div>
                        <div
                          v-else-if="publishRunMetricsState === 'loaded' && publishRunMetrics"
                          class="mt-2"
                        >
                          <table class="run-metrics-table">
                            <thead>
                              <tr>
                                <th>Source</th>
                                <th>Added</th>
                                <th>Removed</th>
                                <th>Updated</th>
                                <th>Made Visible</th>
                                <th>Hidden</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>Feed</td>
                                <td>{{ publishRunMetrics.feed.added }}</td>
                                <td>{{ publishRunMetrics.feed.removed }}</td>
                                <td>{{ publishRunMetrics.feed.changed }}</td>
                                <td>{{ publishRunMetrics.feed.listed }}</td>
                                <td>{{ publishRunMetrics.feed.delisted }}</td>
                              </tr>
                              <tr>
                                <td>Manual</td>
                                <td>{{ publishRunMetrics.manual.added }}</td>
                                <td>{{ publishRunMetrics.manual.removed ?? 0 }}</td>
                                <td>{{ publishRunMetrics.manual.changed }}</td>
                                <td>{{ publishRunMetrics.manual.listed }}</td>
                                <td>{{ publishRunMetrics.manual.delisted }}</td>
                              </tr>
                            </tbody>
                          </table>
                          <div v-if="publishRunMetrics.truncated" class="text-caption text-grey mt-1">
                            Results truncated (safety cap).
                          </div>
                        </div>
                        <div v-else class="text-body-2 text-grey mt-2">
                          Run metrics unavailable for the latest publish.
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </template>
                <template v-else>
                  <div class="text-body-2 text-grey mt-2">
                    No inventory processing has been run yet. Please configure your feed sources and
                    wait for the first processing cycle to complete.
                  </div>
                </template>
              </v-sheet>
            </v-col>

            <v-col cols="12" md="4">
              <v-sheet class="pa-4 h-100 publish-section-card" rounded>
                <div class="font-weight-bold mb-2">Feeds Used</div>
                <div v-if="sourcesUsed.length > 0" class="d-flex flex-column ga-3">
                  <v-sheet v-for="src in sourcesUsed" :key="src.id" rounded class="pa-4 source-card">
                    <div class="d-flex flex-column align-start justify-space-between">
                      <div class="d-flex flex-row align-center justify-space-between w-100 ga-2">
                        <div class="d-flex flex-row align-center flex-wrap ga-2">
                          <span class="mr-2 text-uppercase">{{ src.label }} {{ src.type }}</span>
                          <v-chip
                            v-if="src.feedRoleLabel"
                            size="x-small"
                            variant="tonal"
                            color="grey"
                            class="feed-role-chip"
                          >
                            {{ src.feedRoleLabel }}
                          </v-chip>
                        </div>
                        <v-btn
                          v-if="src.hasData"
                          class="align-self-start"
                          size="small"
                          variant="text"
                          @click="downloadSource(src.id)"
                        >
                          Download
                        </v-btn>
                      </div>
                      <div class="text-caption text-grey mt-1">
                        Total {{ src.displayCount }} • Last:
                        <DateTimeWithDealerTooltip
                          v-if="src.lastProcessedTime"
                          :value="src.lastProcessedTimeRaw"
                          kind="time"
                          :text="src.lastProcessedTime"
                        />
                        <span v-else>N/A</span>
                      </div>
                    </div>
                  </v-sheet>
                </div>
                <div v-else class="text-grey">No sources configured</div>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Activity VINs Dialog -->
    <v-dialog v-model="activityDialog" max-width="800">
      <v-card class="sticky-dialog-card">
        <v-card-title class="text-h6 sticky-dialog-header">{{ activityTitle }}</v-card-title>
        <v-card-text class="sticky-dialog-body">
          <v-alert v-if="activityState === 'error'" type="error" variant="tonal" class="mb-3">
            {{ activityError || 'Failed to load activity VINs.' }}
          </v-alert>

          <v-alert
            v-else-if="activityMeta?.truncated || activityMeta?.vinsTruncated"
            type="warning"
            variant="tonal"
            class="mb-3"
          >
            Showing a capped list of VINs. (Too many results.)
          </v-alert>

          <template v-if="activityState === 'loading'">
            <v-progress-linear indeterminate color="primary" />
          </template>

          <template v-else>
            <div class="text-caption text-grey mb-2">
              {{ activityVins.length }} VIN{{ activityVins.length === 1 ? '' : 's' }}
            </div>
            <div
              v-if="activityMeta?.truncated || activityMeta?.vinsTruncated"
              class="text-caption text-warning mb-2"
            >
              Showing first 500 VINs (capped).
            </div>
            <v-list density="compact" v-if="activityVins.length > 0">
              <v-list-item v-for="v in activityVins" :key="v">
                <v-list-item-title>
                  <a
                    v-if="activityVinsClickable"
                    href="#"
                    class="font-mono vin-link"
                    :aria-label="`Open vehicle details for VIN ${v}`"
                    @click.prevent="goToActivityVin(v)"
                  >
                    {{ v }}
                  </a>
                  <span v-else class="font-mono">{{ v }}</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
            <div v-else class="text-grey">No VINs in this period.</div>
          </template>
        </v-card-text>
        <v-card-actions class="sticky-dialog-footer">
          <v-spacer />
          <v-btn @click="activityDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- History Dialog -->
    <v-dialog v-model="showHistory" max-width="800">
      <v-card class="sticky-dialog-card">
        <v-card-title class="text-h6 sticky-dialog-header">Publish History</v-card-title>
        <v-card-text class="sticky-dialog-body">
          <v-alert v-if="historyError" type="error" variant="tonal" class="mb-3">
            {{ historyError }}
          </v-alert>
          <div class="d-flex align-center ga-3 flex-wrap mb-3">
            <v-text-field
              v-model="publishHistoryCorrelationSearch"
              label="Search correlation ID"
              density="compact"
              variant="outlined"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
              class="flex-grow-1"
              style="max-width: 420px"
            />
            <div class="text-caption text-grey">
              {{ filteredPublishHistoryRows.length }} / {{ publishHistoryRows.length }} runs
            </div>
          </div>
          <v-data-table
            :headers="publishHistoryHeaders"
            :items="filteredPublishHistoryRows"
            density="compact"
            :loading="isLoadingHistory"
            hide-default-header
            class="publish-history-table"
          >
            <template #item.publish="{ item }">
              <div class="publish-history-card">
                <div class="d-flex justify-space-between ga-4">
                  <div class="flex-grow-1" style="min-width: 0;">
                    <div class="d-flex align-center flex-wrap ga-2 publish-history-top">
                      <DateTimeWithDealerTooltip
                        :value="item.timeRaw"
                        :text="item.time"
                        text-class="text-body-2 font-weight-medium"
                      />
                      <v-chip v-if="item.duration" size="x-small" color="grey" variant="outlined">
                        {{ item.duration }}
                      </v-chip>
                      <v-chip
                        size="x-small"
                        :color="
                          String(item.status || '').toUpperCase() === 'SUCCEEDED'
                            ? 'success'
                            : String(item.status || '').toUpperCase() === 'FAILED'
                              ? 'error'
                              : String(item.status || '').toUpperCase() === 'SKIPPED'
                                ? 'warning'
                                : 'info'
                        "
                        variant="flat"
                      >
                        {{ String(item.status || '').toUpperCase() || 'UNKNOWN' }}
                      </v-chip>
                      <v-chip size="x-small" color="info" variant="outlined">
                        {{ item.triggerType }}
                      </v-chip>
                      <v-chip
                        v-if="item.publishAction === 'republish_current_inventory'"
                        size="x-small"
                        color="primary"
                        variant="flat"
                        data-testid="publish-history-republish-indicator"
                      >
                        REPUBLISH CURRENT INVENTORY
                      </v-chip>
                      <v-chip size="x-small" color="grey" variant="outlined">
                        {{ item.user }}
                      </v-chip>
                    </div>
                    <div
                      v-if="item.publishAction === 'republish_current_inventory' && item.publishReason"
                      class="text-caption text-grey-darken-1 mt-1"
                      data-testid="publish-history-republish-reason"
                    >
                      Reason: {{ item.publishReason }}
                    </div>

                    <div
                      class="text-caption text-grey-darken-1 mt-2 d-flex align-center ga-2 publish-history-file-row"
                      style="min-width: 0;"
                    >
                      <span class="text-grey-darken-1">File:</span>
                      <v-btn
                        v-if="item.file"
                        size="x-small"
                        variant="text"
                        class="pa-0 text-none"
                        style="min-width: 0;"
                        :loading="downloadingFileCorrelationId === item.correlationId"
                        append-icon="mdi-download"
                        :aria-label="`Download publish file ${item.file}`"
                        @click="downloadPublishFile(item)"
                      >
                        <span class="text-truncate publish-history-file">
                          {{ item.file }}
                        </span>
                      </v-btn>
                      <span v-else class="text-caption text-grey">—</span>
                    </div>

                    <div
                      class="text-caption text-grey-darken-1 mt-1 d-flex align-center ga-2"
                      style="min-width: 0;"
                    >
                      <span class="text-grey-darken-1">Correlation ID:</span>
                      <span
                        v-if="item.correlationId"
                        class="font-mono text-truncate"
                        style="max-width: 420px"
                        :title="item.correlationId"
                      >
                        {{ item.correlationId }}
                      </span>
                      <span v-else class="text-caption text-grey">—</span>
                    </div>
                  </div>

                  <div class="d-flex ga-2 align-start flex-shrink-0 publish-history-actions">
                  <v-btn
                    v-if="item.correlationId"
                    size="x-small"
                    variant="tonal"
                    color="primary"
                    :to="`/observability/publish/${item.correlationId}`"
                  >
                    View Flow
                  </v-btn>
                  <v-btn
                    v-if="shouldShowViewVins(item)"
                    size="x-small"
                    variant="tonal"
                    color="grey"
                    @click="openPublishVins(item)"
                  >
                    View VINs
                  </v-btn>
                  <v-btn size="x-small" variant="tonal" color="grey" @click="openArtifacts(item)">
                    Settings
                  </v-btn>
                  <v-btn size="x-small" variant="tonal" color="grey" @click="openLogs(item)">Logs</v-btn>
                </div>
              </div>

                <div v-if="item.runMetrics && hasAnyRunMetricsChange(item.runMetrics)" class="mt-3">
                  <table class="run-metrics-table">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Added</th>
                        <th>Removed</th>
                        <th>Updated</th>
                        <th>Made Visible</th>
                        <th>Hidden</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Feed</td>
                        <td>{{ item.runMetrics.feed.added }}</td>
                        <td>{{ item.runMetrics.feed.removed }}</td>
                        <td>{{ item.runMetrics.feed.changed ?? 0 }}</td>
                        <td>{{ item.runMetrics.feed.listed }}</td>
                        <td>{{ item.runMetrics.feed.delisted }}</td>
                      </tr>
                      <tr>
                        <td>Manual</td>
                        <td>{{ item.runMetrics.manual.added }}</td>
                        <td>{{ item.runMetrics.manual.removed ?? 0 }}</td>
                        <td>{{ item.runMetrics.manual.changed ?? 0 }}</td>
                        <td>{{ item.runMetrics.manual.listed }}</td>
                        <td>{{ item.runMetrics.manual.delisted }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="item.runMetrics.truncated" class="text-caption text-grey mt-1">
                    Results truncated (safety cap).
                  </div>
                </div>
                <div v-else-if="item.runMetrics" class="mt-3 text-caption text-grey">
                  No inventory changes in this publish.
                </div>
                <div v-else class="d-flex ga-2 flex-wrap mt-3 publish-history-metrics">
                  <v-chip size="x-small" color="green" variant="flat">Added: {{ item.added }}</v-chip>
                  <v-chip size="x-small" color="error" variant="flat">Removed: {{ item.delisted }}</v-chip>
                  <v-chip v-if="item.removedByPrimarySourceChange > 0" size="x-small" color="error" variant="outlined">
                    Removed (Source): {{ item.removedByPrimarySourceChange }}
                  </v-chip>
                  <v-chip size="x-small" color="success" variant="flat">
                    Made visible: {{ item.opensearchListed }}
                  </v-chip>
                  <v-chip size="x-small" color="warning" variant="flat">
                    Hidden: {{ item.ruleDelisted }}
                  </v-chip>
                  <v-chip size="x-small" color="info" variant="flat">Changed: {{ item.changed }}</v-chip>
                  <v-chip size="x-small" color="grey" variant="flat">Unchanged: {{ item.unchanged }}</v-chip>
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="sticky-dialog-footer">
          <v-spacer />
          <v-btn @click="showHistory = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showRepublishDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-h6">Republish Current Inventory?</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-4">
            This will reprocess the currently published inventory using the currently
            published configuration. Any unpublished changes will remain unpublished.
          </p>
          <v-textarea
            v-model="republishReason"
            label="Reason for republishing"
            variant="outlined"
            density="comfortable"
            rows="3"
            auto-grow
            hide-details="auto"
            data-testid="republish-reason-input"
            :disabled="isRepublishSubmitting"
          />
          <v-alert
            v-if="republishError"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mt-3"
            data-testid="republish-error"
          >
            {{ republishError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="isRepublishSubmitting" @click="closeRepublishDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            data-testid="republish-confirm"
            :disabled="!canSubmitRepublish"
            :loading="isRepublishSubmitting"
            @click="submitRepublish"
          >
            Republish Current Inventory
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Publish VINs Picker -->
    <v-dialog v-model="showPublishVins" max-width="720">
      <v-card>
        <v-card-title class="text-h6">View VINs</v-card-title>
        <v-card-text>
          <div class="text-caption text-grey" v-if="publishVinsItem?.time">
            Publish:
            <DateTimeWithDealerTooltip :value="publishVinsItem.timeRaw" :text="publishVinsItem.time" />
          </div>
          <div class="text-caption text-grey" v-if="publishVinsItem?.correlationId">
            Correlation ID: {{ publishVinsItem.correlationId }}
          </div>

          <v-alert v-if="publishVinsState === 'error'" type="error" variant="tonal" class="mt-3">
            {{ publishVinsError || 'Failed to load VIN lists' }}
          </v-alert>

          <div v-else-if="publishVinsState === 'loading'" class="mt-3">
            <v-progress-linear indeterminate color="primary" />
            <div class="text-caption text-grey mt-2">Loading VIN lists…</div>
          </div>

          <template v-else-if="publishVinsState === 'loaded'">
            <v-alert
              v-if="publishVinsMetrics?.truncated || publishVinsMetrics?.vinsTruncated"
              type="warning"
              variant="tonal"
              class="mt-3"
            >
              Showing a capped list of VINs (safety cap).
            </v-alert>

            <div class="mt-3">
              <div class="text-caption text-grey mb-2">Choose a bucket:</div>

              <div v-if="publishVinsCategories.length > 0" class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="c in publishVinsCategories"
                  :key="c.key"
                  size="small"
                  variant="tonal"
                  color="primary"
                  class="clickable-chip"
                  role="button"
                  tabindex="0"
                  :aria-label="`${c.label}: ${c.count}. Click to view VINs`"
                  @click="openPublishVinsCategory(c)"
                  @keydown.enter.prevent="openPublishVinsCategory(c)"
                  @keydown.space.prevent="openPublishVinsCategory(c)"
                >
                  {{ c.label }} <strong class="ml-1">{{ c.count }}</strong>
                </v-chip>
              </div>
              <div v-else class="text-body-2 text-grey">
                VIN lists unavailable for this publish (counts only).
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="showPublishVins = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Artifacts Dialog -->
    <v-dialog v-model="showArtifacts" max-width="1000">
      <v-card class="sticky-dialog-card">
        <v-card-title class="text-h6 sticky-dialog-header">Publish Settings</v-card-title>
        <v-card-text class="sticky-dialog-body">
          <v-alert v-if="artifactsError" type="error" variant="tonal" class="mb-3">
            {{ artifactsError }}
          </v-alert>
          <v-progress-linear v-if="isLoadingArtifacts" indeterminate color="primary" class="mb-3" />

          <!-- Friendly Summary -->
          <div class="d-flex flex-column ga-3">
              <!-- Publish Rules -->
              <v-card variant="outlined">
                <v-card-text class="py-3">
                  <div class="d-flex justify-space-between align-center">
                    <div class="text-subtitle-2">Publish Rules (archiving)</div>
                  </div>
                  <div class="text-caption text-grey-darken-1 mt-1" v-if="artifactsConfigs.listingRules">
                    If a vehicle matches any <strong>Hide</strong> rule below, it will not be published to the site.
                  </div>

                  <v-expansion-panels v-if="artifactsConfigDetailsFriendly.listingRules" variant="accordion" class="mt-3">
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-caption">
                        <v-icon size="small" class="mr-2">mdi-playlist-check</v-icon>
                        Details
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div
                          v-for="(item, i) in artifactsConfigDetailsFriendly.listingRules.items"
                          :key="i"
                          class="py-1"
                          :style="{ paddingLeft: `${Math.min(item.depth, 6) * 16}px` }"
                        >
                          <div class="d-flex align-center ga-2 flex-wrap">
                            <v-icon size="x-small" v-if="item.kind === 'group'">mdi-folder-outline</v-icon>
                            <v-icon
                              size="x-small"
                              v-else
                              :color="item.action === 'BLOCK' ? 'warning' : item.action === 'ALLOW' ? 'success' : 'grey'"
                            >
                              {{ item.action === 'BLOCK' ? 'mdi-block-helper' : item.action === 'ALLOW' ? 'mdi-check-circle' : 'mdi-information-outline' }}
                            </v-icon>

                            <span class="text-body-2" v-if="item.kind === 'group'">
                              <strong>{{ item.logic === 'AND' ? 'All of these' : item.logic === 'OR' ? 'Any of these' : 'Group' }}</strong>
                            </span>

                            <span class="text-body-2" v-else>
                              {{ item.condition }}
                            </span>

                            <v-chip
                              v-if="item.kind === 'rule' && item.action"
                              size="x-small"
                              :color="item.action === 'BLOCK' ? 'warning' : item.action === 'ALLOW' ? 'success' : 'grey'"
                              variant="flat"
                            >
                              {{ item.action === 'BLOCK' ? 'Hide' : item.action === 'ALLOW' ? 'Allow' : item.action }}
                            </v-chip>

                            <v-chip
                              v-if="item.kind === 'rule' && item.reason"
                              size="x-small"
                              color="grey"
                              variant="outlined"
                            >
                              Why: {{ item.reason }}
                            </v-chip>
                          </div>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
        </v-card-text>
              </v-card>

              <!-- Field Mapping -->
              <v-card variant="outlined">
                <v-card-text class="py-3">
                  <div class="text-subtitle-2">Field Mapping</div>
                  <div class="d-flex ga-2 flex-wrap mt-2" v-if="artifactsConfigs.fieldMapping">
                    <v-chip size="x-small" color="info" variant="flat">
                      Segments: {{ artifactsConfigs.fieldMapping.segmentLabel }}
                    </v-chip>
                    <v-chip size="x-small" color="primary" variant="flat">
                      Mappings: {{ artifactsConfigs.fieldMapping.mappingCount }}
                    </v-chip>
                    <v-chip size="x-small" color="grey" variant="flat" v-if="artifactsConfigs.fieldMapping.sourceLabel">
                      Sources: {{ artifactsConfigs.fieldMapping.sourceLabel }}
                    </v-chip>
                  </div>
                  <!-- Intentionally omit verbose mapping stats here; the Details table provides the authoritative mapping list. -->

                  <v-expansion-panels v-if="artifactsConfigDetailsFriendly.fieldMapping" variant="accordion" class="mt-3">
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-caption">
                        <v-icon size="small" class="mr-2">mdi-map</v-icon>
                        Details
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div class="d-flex flex-column ga-3">
                          <!-- Intentionally omit summary lines; the table below is the authoritative view. -->

                          <v-text-field
                            v-model="fieldMappingSearch"
                            label="Filter fields (e.g. vin, price, make)"
                            density="compact"
                            variant="outlined"
                            hide-details
                          />

                          <v-data-table
                            :headers="fieldMappingHeaders"
                            :items="artifactsConfigDetailsFriendly.fieldMapping.rows"
                            :search="fieldMappingSearch"
                            density="compact"
                            items-per-page="15"
                          >
                            <template #item.source="{ item }">
                              <v-chip size="x-small" color="primary" variant="outlined">
                                {{ item.source }}
                              </v-chip>
                            </template>
                          </v-data-table>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>

              <!-- Grouping -->
              <v-card variant="outlined">
                <v-card-text class="py-3">
                  <div class="text-subtitle-2">Grouping</div>
                  <div class="d-flex ga-2 flex-wrap mt-2" v-if="artifactsConfigs.segmentsOrGrouping">
                    <v-chip size="x-small" color="info" variant="flat">
                      Segments: {{ artifactsConfigs.segmentsOrGrouping.segmentLabel }}
                    </v-chip>
                  </div>

                  <v-expansion-panels v-if="artifactsConfigDetailsFriendly.segmentsOrGrouping" variant="accordion" class="mt-3">
                    <v-expansion-panel>
                      <v-expansion-panel-title class="text-caption">
                        <v-icon size="small" class="mr-2">mdi-shape</v-icon>
                        Details
                      </v-expansion-panel-title>
                      <v-expansion-panel-text>
                        <div class="d-flex flex-column ga-2">
                          <div class="text-caption text-grey-darken-1">
                            A vehicle belongs to a segment if it matches that segment’s rules.
                          </div>

                          <v-expansion-panels
                            variant="accordion"
                            v-if="artifactsConfigDetailsFriendly.segmentsOrGrouping.segments?.length"
                          >
                            <v-expansion-panel
                              v-for="seg in artifactsConfigDetailsFriendly.segmentsOrGrouping.segments"
                              :key="seg.segmentId"
                            >
                              <v-expansion-panel-title class="text-caption">
                                <v-icon size="small" class="mr-2">mdi-tag-outline</v-icon>
                                {{ seg.segmentName }}
                                <span class="text-grey-darken-1 ml-2">({{ seg.segmentId }})</span>
                              </v-expansion-panel-title>
                              <v-expansion-panel-text>
                                <div v-if="!seg.items.length" class="text-caption text-grey-darken-1">
                                  No rules (everything matches).
                                </div>
                                <div
                                  v-for="(item, i) in seg.items"
                                  :key="i"
                                  class="py-1"
                                  :style="{ paddingLeft: `${Math.min(item.depth, 6) * 16}px` }"
                                >
                                  <div class="d-flex align-center ga-2 flex-wrap">
                                    <v-icon size="x-small" v-if="item.kind === 'group'">mdi-folder-outline</v-icon>
                                    <v-icon size="x-small" v-else color="grey">mdi-information-outline</v-icon>

                                    <span class="text-body-2" v-if="item.kind === 'group'">
                                      <strong>{{ item.logic === 'AND' ? 'All of these' : item.logic === 'OR' ? 'Any of these' : 'Group' }}</strong>
                                    </span>

                                    <span class="text-body-2" v-else>
                                      {{ item.condition }}
                                    </span>
                                  </div>
                                </div>
                              </v-expansion-panel-text>
                            </v-expansion-panel>
                          </v-expansion-panels>
                        </div>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>

          </div>

          <!-- Raw JSON (dev-only debugging) -->
          <v-expansion-panels v-if="showDebugPanels && artifactsJson" variant="accordion">
            <v-expansion-panel>
              <v-expansion-panel-title class="text-caption">
                <v-icon size="small" class="mr-2">mdi-code-json</v-icon>
                Raw JSON Settings (for debugging)
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <pre class="debug-pre">{{ artifactsJson }}</pre>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="sticky-dialog-footer">
          <v-spacer />
          <v-btn @click="showArtifacts = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Logs Dialog -->
    <v-dialog v-model="showLogs" max-width="1000">
      <v-card class="sticky-dialog-card">
        <v-card-title class="text-h6 sticky-dialog-header">Publish Logs</v-card-title>
        <v-card-text class="sticky-dialog-body">
          <PublishLogsTimeline
            :logs="humanReadableLogs"
            :raw-json="logsJson"
            :loading="isLoadingLogs"
            :error="logsError"
            :show-debug-panels="showDebugPanels"
          />
        </v-card-text>
        <v-card-actions class="sticky-dialog-footer">
          <v-spacer />
          <v-btn @click="showLogs = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, inject, onMounted, watch, type Ref } from 'vue'
import { getCurrentInventoryStats, getInventoryCountSummary, getInventoryActivityCounts } from '@/api'
import {
  fetchInventory,
  fetchPublishRunMetrics,
  type PublishRunMetrics,
  type WebsiteVisibility,
} from '@/services/inventory'
import {
  getSourcesProcessDetails,
  getSourceStats,
  getAllProcessingJobs,
  getTodayProcessingJobs,
} from '@/services/feedSourceService'
import { getPublishArtifacts, getPublishLogs } from '@/services/publishHistoryService'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useSourcesStore } from '@/stores/sourcesStore'
import { usePublishStore } from '@/stores/usePublishStore'
import { useSegmentStore } from '@/stores/segment'
import { useRouter } from 'vue-router'
import PublishLogsTimeline from '@/components/PublishLogsTimeline.vue'
import { parsePublishLogsToHumanReadable, type PublishHumanLog } from '@/utils/publishLogsTimeline'
import { filterPublishHistoryRowsByCorrelationId } from '@/utils/publishHistoryFilter'
import { formatDateTimeWithTzShort, formatTimeWithTzShort } from '@/utils'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'

interface Segment {
  segmentId: string
  count: number
  vins?: string[]
}

const accountStore = useAccountSettingsStore()
const sourcesStore = useSourcesStore()
const publishStore = usePublishStore()
const segmentStore = useSegmentStore()
const endpoint = import.meta.env.VITE_ISE_BACKEND_URL
const router = useRouter()
const globalIsProcessing = inject<Ref<boolean>>('isProcessing', ref(false))
const showRepublishDialog = ref(false)
const republishReason = ref('')
const republishError = ref('')
const isRepublishSubmitting = ref(false)
const isRepublishDisabled = computed(
  () =>
    globalIsProcessing.value === true ||
    publishStore.hasPendingPublish === true ||
    publishStore.hasPendingRepublish === true,
)
const canSubmitRepublish = computed(
  () => republishReason.value.trim().length > 0 && !isRepublishSubmitting.value && !isRepublishDisabled.value,
)

const openRepublishDialog = () => {
  republishReason.value = ''
  republishError.value = ''
  showRepublishDialog.value = true
}

const closeRepublishDialog = () => {
  if (isRepublishSubmitting.value) return
  showRepublishDialog.value = false
  republishReason.value = ''
  republishError.value = ''
}

const submitRepublish = async () => {
  const reason = republishReason.value.trim()
  if (!reason || isRepublishDisabled.value) return

  isRepublishSubmitting.value = true
  republishError.value = ''
  try {
    const ok = await publishStore.republishCurrentInventory(endpoint, accountStore.accountId, reason)
    if (ok) {
      showRepublishDialog.value = false
      republishReason.value = ''
      return
    }
    const publishError = publishStore.lastRepublishError
    republishError.value = Array.isArray(publishError?.messages)
      ? publishError.messages.join(' ')
      : publishError?.message || 'Failed to republish current inventory. Please try again.'
  } catch (error) {
    console.error('Error republishing current inventory:', error)
    republishError.value = 'Failed to republish current inventory. Please try again.'
  } finally {
    isRepublishSubmitting.value = false
  }
}

// Avoid flashing draft-only segments on the Dashboard.
// Other screens load segments with includeDraft=true; we only want published segments here.
const dashboardSegmentsReady = ref(false)

const goToInventoryWithSegment = (segment: 'new' | 'used' | 'cpo') => {
  router.push({
    name: 'Inventory',
    query: { segment },
  })
}

const goToInventoryWithWebsiteVisibility = (websiteVisibility: WebsiteVisibility) => {
  router.push({
    name: 'Inventory',
    query: { websiteVisibility },
  })
}

const goToInventoryWithCustomSegment = (segment: string) => {
  const id = String(segment || '').trim()
  if (!id) return
  router.push({
    name: 'Inventory',
    query: { segment: id },
  })
}

const goToInventoryWithType = (type: 'auto' | 'manual') => {
  router.push({
    name: 'Inventory',
    query: { type },
  })
}

const goToAccountConfiguration = () => {
  router.push({ name: 'AccountConfiguration' })
}

const data = ref<any>({
  total: null,
  new: null,
  used: null,
  cpo: null,
})

// Published vehicles (only those with SRP objects / in OpenSearch)
const publishedTotal = ref<number | null>(null)
const currentInventoryListed = ref<number | null>(null)
const currentInventoryDelisted = ref<number | null>(null)
const inventoryCountState = ref<'idle' | 'loading' | 'loaded' | 'error'>('idle')

const stats = computed(() => {
  const v = data.value
  if (!v || typeof v !== 'object') {
    return { total: 0, new: 0, used: 0, cpo: 0 }
  }

  if ((v as any).segments == null) {
    // Legacy shape fallback (older endpoints)
    return v
  }

  const byId = (id: string) =>
    ((v as any).segments as Segment[])
      .find((s) => String(s.segmentId || '').toLowerCase() === id)
      ?.count ?? 0

  return {
    total: byId('all'),
    new: byId('new'),
    used: byId('used'),
    cpo: byId('cpo'),
  }
})

const sourceBreakdown = computed(() => {
  const v = data.value as any
  const sc = v?.sourceCounts
  const manual = typeof sc?.manual === 'number' ? sc.manual : null
  const feed = typeof sc?.feed === 'number' ? sc.feed : null
  return { feed, manual }
})

const websiteVisibilityBreakdown = computed(() => {
  const v = data.value as any
  const counts = v?.websiteVisibilityCounts
  const visible = typeof counts?.VISIBLE === 'number' ? counts.VISIBLE : null
  const hidden = typeof counts?.HIDDEN === 'number' ? counts.HIDDEN : null
  return { visible, hidden }
})

const manualDraftAdds = computed(() => {
  const v = data.value as any
  const dc = v?.draftCounts
  return typeof dc?.manualAdds === 'number' ? dc.manualAdds : null
})

// Dynamic ISE data from API (latest processed snapshot)
// - added/removed: latest run membership delta (from processing details reportData.counts)
// - visible/hidden: snapshot counts from CurrentInventory.websiteVisibility
const ise = ref({
  lastRunRaw: null as string | number | Date | null,
  lastRun: '',
  correlationId: '',
  added: 0,
  removed: 0,
  triggerType: '' as 'feed' | 'user' | 'unknown' | '',
  triggeredBy: '',
})
const hasProcessedData = ref(false)

// ── Feed Guardrail banner state ───────────────────────────────────────────────
const flaggedJob = ref<any | null>(null)
const guardrailOverrideLoading = ref<'approve' | 'discard' | null>(null)
const guardrailOverrideError = ref('')
// ─────────────────────────────────────────────────────────────────────────────

const iseListed = computed(() =>
  typeof currentInventoryListed.value === 'number' ? currentInventoryListed.value : 0,
)
const iseDelisted = computed(() =>
  typeof currentInventoryDelisted.value === 'number' ? currentInventoryDelisted.value : 0,
)

const publishRunMetricsState = ref<'idle' | 'loading' | 'loaded' | 'error'>('idle')
const publishRunMetricsError = ref('')
const publishRunMetrics = ref<PublishRunMetrics | null>(null)
const latestInventoryUpdateState = ref<'idle' | 'loading' | 'loaded' | 'error'>('idle')
const latestInventoryUpdateError = ref('')

// ISE Last Processed tiles:
// Prefer “from this publish” (correlationId) metrics when available; fall back to legacy values.
const iseAddedBox = computed(() => {
  const m = publishRunMetrics.value
  if (m) return (m.feed.added || 0) + (m.manual.added || 0)
  return ise.value.added || 0
})

const iseRemovedBox = computed(() => {
  const m = publishRunMetrics.value
  if (m) return (m.feed.removed || 0) + (m.manual.removed || 0)
  return ise.value.removed || 0
})

const iseUpdatedBox = computed(() => {
  const m = publishRunMetrics.value
  if (m) return (m.feed.changed || 0) + (m.manual.changed || 0)
  // Fallback to processing-details aggregation (best-effort)
  return (latestChanges.value.updated || []).length
})

const iseListedBox = computed(() => {
  const m = publishRunMetrics.value
  if (m) return (m.feed.listed || 0) + (m.manual.listed || 0)
  return iseListed.value || 0
})

const iseDelistedBox = computed(() => {
  const m = publishRunMetrics.value
  if (m) return (m.feed.delisted || 0) + (m.manual.delisted || 0)
  return iseDelisted.value || 0
})

// Activity (unique VINs per status) for Today / Week / Month
const runsToday = ref(0)
const todayAdded = ref(0)
const todayRemoved = ref(0)
const weekAdded = ref(0)
const weekRemoved = ref(0)
const monthAdded = ref(0)
const monthRemoved = ref(0)
// Net change = Added - Removed (for Today’s Activity)
const netChange = computed(() => (todayAdded.value || 0) - (todayRemoved.value || 0))
const todaySummary = computed(() => {
  const a = todayAdded.value || 0
  const r = todayRemoved.value || 0
  const total = stats.value.total ?? 0
  const net = a - r
  const percentDrop = total > 0 ? (r - a) / total : 0
  const largeNegativeDrop = net < 0 && (r - a >= 10 || percentDrop >= 0.05)

  if (a === 0 && r === 0) return 'No inventory changes today.'
  if (largeNegativeDrop) return 'Inventory is down today. Verify feed status.'
  if (net < 0) return 'Inventory is down today. Check sold units or feed changes.'
  if (net > 0) return `Inventory is up by ${net} vehicle${net === 1 ? '' : 's'} today.`
  return 'No inventory changes today.'
})
const openAlerts = ref<Array<{ id: string; text: string }>>([])
const resolveAlerts = () => {
  /* future: route to resolving workflow */
}

type ActivityPeriod = 'today' | 'week' | 'month'
type ActivityKind = 'added' | 'removed'

const activityDialog = ref(false)
const activityTitle = ref('')
const activityState = ref<'idle' | 'loading' | 'loaded' | 'error'>('idle')
const activityError = ref('')
const activityVins = ref<string[]>([])
const activityMeta = ref<{ truncated?: boolean; vinsTruncated?: boolean } | null>(null)
const activityVinsClickable = ref(true)

function getActivityRange(period: ActivityPeriod): { startIso: string; endIso: string } {
  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)

  if (period === 'today') return { startIso: startOfDay.toISOString(), endIso: end.toISOString() }

  if (period === 'week') {
    const startOfWeek = new Date(startOfDay)
    // week starts on Sunday to match JS Date; adjust if you prefer Monday
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
    return { startIso: startOfWeek.toISOString(), endIso: end.toISOString() }
  }

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  return { startIso: startOfMonth.toISOString(), endIso: end.toISOString() }
}

const openVinDialog = (params: {
  title: string
  vins: string[]
  truncated?: boolean
  vinsTruncated?: boolean
  vinsClickable?: boolean
}) => {
  activityDialog.value = true
  activityTitle.value = params.title
  activityState.value = 'loaded'
  activityError.value = ''
  activityVins.value = Array.isArray(params.vins) ? params.vins : []
  activityMeta.value = { truncated: params.truncated, vinsTruncated: params.vinsTruncated }
  activityVinsClickable.value = params.vinsClickable !== false
}

const openActivity = async (period: ActivityPeriod, kind: ActivityKind) => {
  if (!accountStore.accountId) return

  activityDialog.value = true
  activityState.value = 'loading'
  activityError.value = ''
  activityVins.value = []
  activityMeta.value = null
  // VINs in the activity dialog should deep-link to Vehicle Details (even for removals).
  activityVinsClickable.value = true

  const labelPeriod = period === 'today' ? "Today" : period === 'week' ? "This week" : "This month"
  const labelKind = kind === 'added' ? 'Added VINs' : 'Removed VINs'
  activityTitle.value = `${labelPeriod} — ${labelKind}`

  try {
    const range = getActivityRange(period)
    const res = await getInventoryActivityCounts({
      endpoint,
      accountId: accountStore.accountId,
      startIso: range.startIso,
      endIso: range.endIso,
      includeVins: true,
      maxVins: 500,
    })

    const vins = kind === 'added' ? res?.vins?.added : res?.vins?.removed
    activityVins.value = Array.isArray(vins) ? vins : []
    activityMeta.value = { truncated: res?.truncated, vinsTruncated: res?.vinsTruncated }
    activityState.value = 'loaded'
  } catch (e) {
    activityError.value = e instanceof Error ? e.message : String(e)
    activityState.value = 'error'
  }
}

const goToActivityVin = async (v: string) => {
  if (!activityVinsClickable.value) return
  const vin = String(v || '').trim()
  if (!vin) return
  activityDialog.value = false
  await router.push(`/inventory/${vin}`)
}

const fetchSnapshotVinsByWebsiteVisibility = async (websiteVisibility: WebsiteVisibility) => {
  const jazelId = accountStore.accountId
  if (!jazelId) return { vins: [] as string[], truncated: false }

  // Cap at 500 VINs to keep payloads and dialog manageable
  const limit = 500
  const qs = new URLSearchParams({
    jazelId,
    websiteVisibility,
    limit: String(limit),
    offset: '0',
  })

  const resp = await fetch(`${endpoint}/inventory?${qs.toString()}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  })
  const json = (await resp.json().catch(() => ({}))) as unknown
  if (!resp.ok) {
    const msg =
      typeof (json as any)?.details === 'string'
        ? (json as any).details
        : `Failed to load inventory (${resp.status})`
    throw new Error(msg)
  }

  const vehicles = Array.isArray((json as any)?.vehicles) ? ((json as any).vehicles as any[]) : []
  const vins = vehicles.map((v) => String(v?.vin || '').trim().toUpperCase()).filter(Boolean)
  const total = (json as any)?.pagination?.total
  const truncated = typeof total === 'number' ? total > limit : vins.length >= limit
  return { vins, truncated }
}

const fetchSnapshotDraftVins = async () => {
  const jazelId = accountStore.accountId
  if (!jazelId) return { vins: [] as string[], truncated: false }

  // Cap at 500 VINs to keep payloads and dialog manageable
  const limit = 500
  const resp = await fetchInventory(endpoint, {
    jazelId,
    limit,
    offset: 0,
    type: 'manual',
    includeArchived: false,
  })

  const vehicles = Array.isArray(resp?.vehicles) ? resp.vehicles : []
  const vins = vehicles
    .filter((v: any) => v?.publishState === 'draft')
    .map((v: any) => String(v?.vin || '').trim().toUpperCase())
    .filter(Boolean)

  // Best-effort truncation indicator: we only know we capped if we actually hit the cap for drafts.
  // (We don't have a backend query for "drafts only" yet.)
  const truncated = vins.length >= limit
  return { vins, truncated }
}

const openWebsiteVisibilityMetric = async (kind: 'hidden' | 'drafts') => {
  const count = kind === 'hidden' ? hiddenCountSafe.value : draftCountSafe.value
  if (!count || count <= 0) return

  activityDialog.value = true
  activityTitle.value = `Website visibility — ${kind === 'hidden' ? 'Hidden VINs' : 'Draft VINs'}`
  activityState.value = 'loading'
  activityError.value = ''
  activityVins.value = []
  activityMeta.value = null
  activityVinsClickable.value = true

  try {
    const res =
      kind === 'hidden' ? await fetchSnapshotVinsByWebsiteVisibility('HIDDEN') : await fetchSnapshotDraftVins()
    activityVins.value = res.vins
    activityMeta.value = { truncated: res.truncated, vinsTruncated: res.truncated }
    activityState.value = 'loaded'
  } catch (e) {
    activityError.value = e instanceof Error ? e.message : String(e)
    activityState.value = 'error'
  }
}

const openIseMetric = async (kind: 'added' | 'removed' | 'updated' | 'listed' | 'delisted') => {
  // Only open when the metric is > 0 (per requirement)
  const count =
    kind === 'added'
      ? iseAddedBox.value
      : kind === 'removed'
        ? iseRemovedBox.value
        : kind === 'updated'
          ? iseUpdatedBox.value
        : kind === 'listed'
          ? iseListedBox.value
          : iseDelistedBox.value
  if (!count || count <= 0) return

  const runVins = publishRunMetrics.value?.vins || null

  if (kind === 'added') {
    if (runVins) {
      const merged = Array.from(new Set([...(runVins.feed.added || []), ...(runVins.manual.added || [])])).sort()
      openVinDialog({
        title: `ISE Last Processed — Added VINs`,
        vins: merged,
        truncated: publishRunMetrics.value?.truncated,
        vinsTruncated: publishRunMetrics.value?.vinsTruncated,
        vinsClickable: true,
      })
      return
    }
    openVinDialog({
      title: `ISE Last Processed — Added VINs`,
      vins: latestChanges.value.added,
      vinsClickable: true,
    })
    return
  }
  if (kind === 'removed') {
    if (runVins) {
      const merged = Array.from(
        new Set([...(runVins.feed.removed || []), ...(((runVins.manual as any)?.removed as string[]) || [])]),
      ).sort()
      openVinDialog({
        title: `ISE Last Processed — Removed VINs`,
        vins: merged,
        truncated: publishRunMetrics.value?.truncated,
        vinsTruncated: publishRunMetrics.value?.vinsTruncated,
        vinsClickable: false,
      })
      return
    }
    openVinDialog({
      title: `ISE Last Processed — Removed VINs`,
      vins: latestChanges.value.removed,
      vinsClickable: false,
    })
    return
  }

  if (kind === 'updated') {
    if (runVins) {
      const merged = Array.from(
        new Set([...(runVins.feed.changed || []), ...(runVins.manual.changed || [])]),
      ).sort()
      openVinDialog({
        title: `ISE Last Processed — Updated VINs`,
        vins: merged,
        truncated: publishRunMetrics.value?.truncated,
        vinsTruncated: publishRunMetrics.value?.vinsTruncated,
        vinsClickable: true,
      })
      return
    }

    openVinDialog({
      title: `ISE Last Processed — Updated VINs`,
      vins: (latestChanges.value.updated || []).map((v) => v.vin).filter(Boolean),
      vinsClickable: true,
    })
    return
  }

  if (runVins) {
    const merged =
      kind === 'listed'
        ? Array.from(new Set([...(runVins.feed.listed || []), ...(runVins.manual.listed || [])])).sort()
        : Array.from(new Set([...(runVins.feed.delisted || []), ...(runVins.manual.delisted || [])])).sort()

    openVinDialog({
      title: `ISE Last Processed — ${kind === 'listed' ? 'Made visible VINs' : 'Hidden VINs'}`,
      vins: merged,
      truncated: publishRunMetrics.value?.truncated,
      vinsTruncated: publishRunMetrics.value?.vinsTruncated,
      vinsClickable: true,
    })
    return
  }

  // Fallback (legacy snapshot list)
  activityDialog.value = true
  activityTitle.value = `ISE Last Processed — ${kind === 'listed' ? 'Made visible VINs' : 'Hidden VINs'}`
  activityState.value = 'loading'
  activityError.value = ''
  activityVins.value = []
  activityMeta.value = null
  activityVinsClickable.value = true
  try {
    const res = await fetchSnapshotVinsByWebsiteVisibility(kind === 'listed' ? 'VISIBLE' : 'HIDDEN')
    activityVins.value = res.vins
    activityMeta.value = { truncated: res.truncated, vinsTruncated: res.truncated }
    activityState.value = 'loaded'
  } catch (e) {
    activityError.value = e instanceof Error ? e.message : String(e)
    activityState.value = 'error'
  }
}

const showHistory = ref(false)
const isLoadingHistory = ref(false)
const historyError = ref('')
const publishHistoryCorrelationSearch = ref('')

type PublishVinsState = 'idle' | 'loading' | 'loaded' | 'error'
type PublishVinsCategory = { key: string; label: string; count: number; vins: string[] }

const showPublishVins = ref(false)
const publishVinsState = ref<PublishVinsState>('idle')
const publishVinsError = ref('')
const publishVinsItem = ref<any | null>(null)
const publishVinsCache = ref<Record<string, PublishRunMetrics>>({})

const publishJobs = ref<any[]>([])
const hasPublishHistory = computed(() => publishJobs.value.length > 0)

const latestChanges = ref({
  added: [] as string[],
  updated: [] as Array<{ vin: string; changes: string }>,
  removed: [] as string[],
})

const publishHistoryHeaders = [{ title: 'Publish', value: 'publish', sortable: false }]

const publishHistoryRows = computed(() => {
  return (publishJobs.value || []).map((job: any) => {
    const ts = job.updatedAt || job.createdAt
    const time = ts ? formatDateTimeWithTzShort(ts) : ''
    const startedAtIso = String(job.startedAt || job.createdAt || '')
    const finishedAtIso = String(job.finishedAt || '')
    let duration = ''
    if (startedAtIso && finishedAtIso) {
      const ms = new Date(finishedAtIso).getTime() - new Date(startedAtIso).getTime()
      if (!Number.isNaN(ms) && ms >= 0) duration = `${Math.round(ms / 1000)}s`
    } else if (startedAtIso && !finishedAtIso) {
      // still running: show elapsed since startedAt (best-effort)
      const ms = Date.now() - new Date(startedAtIso).getTime()
      if (!Number.isNaN(ms) && ms >= 0) duration = `${Math.round(ms / 1000)}s`
    }
    const fileKey = job.fileKey || job.result?.fileKey || ''
    const file = typeof fileKey === 'string' && fileKey.includes('/') ? fileKey.split('/').pop() : fileKey
    const result = job.result || {}
    const triggerType = job.triggerType || 'unknown'
    const requestedByUsername = job.requestedByUsername
    // Display name for who/what triggered the publish:
    // - user-triggered: show verified username (fallback "anonymous")
    // - feed-triggered: show "feed" (even if legacy jobs stored "system")
    // - unknown: best-effort; default to "feed" so automated runs are not mislabeled as a person
    const triggeredBy =
      triggerType === 'feed'
        ? 'feed'
        : triggerType === 'user'
          ? requestedByUsername || 'anonymous'
          : requestedByUsername || 'feed'
    return {
      correlationId: job.correlationId,
      jazelId: job.jazelId,
      timeRaw: ts,
      time,
      duration,
      status: job.status || '',
      triggerType,
      publishAction: job.publishAction,
      publishReason: job.publishReason,
      user: triggeredBy,
      file: file || '',
      fileKey: fileKey || '',
      added: result.listed ?? 0,
      opensearchListed: result.opensearchListed ?? 0,
      delisted: result.delisted ?? 0,
      ruleDelisted: result.ruleDelisted ?? 0,
      removedByPrimarySourceChange: result.removedByPrimarySourceChange ?? 0,
      changed: result.changed ?? 0,
      unchanged: result.unchanged ?? 0,
      runMetrics: result.runMetrics || null,
      _job: job,
    }
  })
})

const filteredPublishHistoryRows = computed(() =>
  filterPublishHistoryRowsByCorrelationId(publishHistoryRows.value || [], publishHistoryCorrelationSearch.value),
)

const hasAnyRunMetricsChange = (m: any): boolean => {
  if (!m) return false
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0)
  const values = [
    n(m.feed?.added),
    n(m.feed?.removed),
    n(m.feed?.changed),
    n(m.feed?.listed),
    n(m.feed?.delisted),
    n(m.manual?.added),
    n(m.manual?.removed),
    n(m.manual?.changed),
    n(m.manual?.listed),
    n(m.manual?.delisted),
  ]
  return values.some((v) => v > 0)
}

const hasLatestInventoryUpdate = computed(() => hasProcessedData.value || !!ise.value.correlationId)

const resetPublishRunMetrics = () => {
  publishRunMetricsState.value = 'idle'
  publishRunMetricsError.value = ''
  publishRunMetrics.value = null
}

const loadPublishRunMetricsForCorrelation = async (
  url: string,
  accountId: string,
  correlationId: string,
) => {
  resetPublishRunMetrics()
  if (!correlationId) return

  publishRunMetricsState.value = 'loading'
  try {
    const runRes = await fetchPublishRunMetrics(url, accountId, correlationId, {
      includeVins: true,
      maxVins: 500,
    })
    publishRunMetrics.value = runRes?.metrics || null
    publishRunMetricsState.value = 'loaded'
  } catch (e) {
    publishRunMetricsError.value = e instanceof Error ? e.message : String(e)
    publishRunMetricsState.value = 'error'
  }
}

const publishJobTimestamp = (job: any): string => String(job?.finishedAt || job?.updatedAt || job?.createdAt || '').trim()

const resolvePublishTrigger = (job: any): {
  triggerType: 'feed' | 'user' | 'unknown'
  triggeredBy: string
} => {
  const triggerType: 'feed' | 'user' | 'unknown' =
    job?.triggerType === 'feed' || job?.triggerType === 'user' ? job.triggerType : 'unknown'
  const requestedByUsername = String(job?.requestedByUsername || '').trim()
  return {
    triggerType,
    triggeredBy:
      triggerType === 'feed' ? 'feed' : triggerType === 'user' ? requestedByUsername || 'anonymous' : 'feed',
  }
}

const loadCurrentInventoryDashboardStats = async (url: string, accountId: string) => {
  try {
    data.value = await getCurrentInventoryStats(url, accountId)
    console.log('Inventory Stats (all segmented vehicles):', data.value)
    // Keep snapshot refs in sync for downstream cards until /inventory-count resolves.
    currentInventoryListed.value =
      typeof (data.value as any)?.websiteVisibilityCounts?.VISIBLE === 'number'
        ? (data.value as any).websiteVisibilityCounts.VISIBLE
        : null
    currentInventoryDelisted.value =
      typeof (data.value as any)?.websiteVisibilityCounts?.HIDDEN === 'number'
        ? (data.value as any).websiteVisibilityCounts.HIDDEN
        : null
  } catch (e) {
    console.error('[Dashboard] getCurrentInventoryStats failed:', e)
    // Safe fallback to keep dashboard alive.
    data.value = {
      jazelId: accountId,
      segments: [],
      totalVehicles: 0,
      counts: { all: 0, new: 0, used: 0, cpo: 0 },
      sourceCounts: { feed: 0, manual: 0 },
      websiteVisibilityCounts: { VISIBLE: 0, HIDDEN: 0 },
    }
    currentInventoryListed.value = null
    currentInventoryDelisted.value = null
  }

  inventoryCountState.value = 'loading'
  try {
    const counts = await getInventoryCountSummary(url, accountId)
    const current = counts.currentInventory ?? {}
    const onWebsite = current.visibleOnWebsite ?? 0
    const notOnWebsite = current.hiddenFromWebsite ?? 0

    publishedTotal.value = onWebsite
    currentInventoryListed.value = onWebsite
    currentInventoryDelisted.value = notOnWebsite
    inventoryCountState.value = 'loaded'
  } catch (e) {
    console.error('[Dashboard] getInventoryCountSummary failed:', e)
    inventoryCountState.value = 'error'
    // Do NOT throw; keep dashboard rendered.
    publishedTotal.value = null
  }
}

const hasAnyLegacyChange = (item: any): boolean => {
  if (!item) return false
  const n = (v: unknown) => (typeof v === 'number' ? v : Number(v) || 0)
  const values = [
    n(item.added),
    n(item.delisted),
    n(item.changed),
    n(item.opensearchListed),
    n(item.ruleDelisted),
    n(item.removedByPrimarySourceChange),
  ]
  return values.some((v) => v > 0)
}

const shouldShowViewVins = (item: any): boolean => {
  if (!item?.correlationId) return false
  if (item.runMetrics) return hasAnyRunMetricsChange(item.runMetrics)
  return hasAnyLegacyChange(item)
}

const publishVinsMetrics = computed(() => {
  const cid = String(publishVinsItem.value?.correlationId || '').trim()
  if (!cid) return null
  return publishVinsCache.value[cid] || null
})

const publishVinsCategories = computed<PublishVinsCategory[]>(() => {
  const m = publishVinsMetrics.value
  const vins = m?.vins
  if (!vins) return []

  const out: PublishVinsCategory[] = []
  const add = (key: string, label: string, list: unknown) => {
    const arr = Array.isArray(list) ? (list as string[]).map((v) => String(v || '').trim()).filter(Boolean) : []
    if (arr.length === 0) return
    out.push({ key, label, count: arr.length, vins: arr })
  }

  add('feed.added', 'Feed — Added', vins.feed?.added)
  add('feed.removed', 'Feed — Removed', vins.feed?.removed)
  add('feed.changed', 'Feed — Updated', vins.feed?.changed)
  add('feed.listed', 'Feed — Made Visible', vins.feed?.listed)
  add('feed.delisted', 'Feed — Hidden', vins.feed?.delisted)

  add('manual.added', 'Manual — Added', vins.manual?.added)
  add('manual.removed', 'Manual — Removed', (vins.manual as any)?.removed)
  add('manual.changed', 'Manual — Updated', vins.manual?.changed)
  add('manual.listed', 'Manual — Made Visible', vins.manual?.listed)
  add('manual.delisted', 'Manual — Hidden', vins.manual?.delisted)

  return out
})

const openPublishVins = async (item: any) => {
  const cid = String(item?.correlationId || '').trim()
  const jazelId = accountStore.accountId
  if (!cid || !jazelId) return

  publishVinsItem.value = item
  showPublishVins.value = true
  publishVinsError.value = ''

  const cached = publishVinsCache.value[cid]
  if (cached?.vins) {
    publishVinsState.value = 'loaded'
    return
  }

  publishVinsState.value = 'loading'
  try {
    const res = await fetchPublishRunMetrics(endpoint, jazelId, cid, { includeVins: true, maxVins: 500 })
    const metrics = res?.metrics || null
    if (!metrics) throw new Error('No run metrics returned')
    publishVinsCache.value = { ...publishVinsCache.value, [cid]: metrics }
    publishVinsState.value = 'loaded'
  } catch (e) {
    publishVinsError.value = e instanceof Error ? e.message : String(e)
    publishVinsState.value = 'error'
  }
}

const openPublishVinsCategory = (c: PublishVinsCategory) => {
  const list = Array.isArray(c?.vins) ? c.vins : []
  if (list.length === 0) return
  const m = publishVinsMetrics.value
  showPublishVins.value = false
  openVinDialog({
    title: `Publish VINs — ${c.label}`,
    vins: list,
    truncated: m?.truncated,
    vinsTruncated: m?.vinsTruncated,
    vinsClickable: true,
  })
}

const showArtifacts = ref(false)
const artifactsJson = ref('')
const artifactsObj = ref<any | null>(null)
const artifactsError = ref('')
const isLoadingArtifacts = ref(false)
const showDebugPanels = import.meta.env.DEV
const downloadingFileCorrelationId = ref<string>('')
const fieldMappingSearch = ref('')
const fieldMappingHeaders = [
  { title: 'Segment', key: 'segment', sortable: true },
  { title: 'Field', key: 'field', sortable: true },
  { title: 'Source', key: 'source', sortable: true },
]

const showLogs = ref(false)
const logsJson = ref('')
const logsError = ref('')
const isLoadingLogs = ref(false)
const humanReadableLogs = ref<PublishHumanLog[]>([])

// --- Website visibility helpers ---
const onWebsiteCount = computed(() => (typeof publishedTotal.value === 'number' ? publishedTotal.value : null))

const websiteVisibilityPct = computed(() => {
  const total = stats.value.total
  const on = publishedTotal.value
  if (typeof total !== 'number' || total <= 0) return null
  if (typeof on !== 'number' || on < 0) return null
  const pct = (on / total) * 100
  // pick one:
  return Math.round(pct)               // 99
  // return Math.round(pct * 10) / 10  // 99.5
})

// Hidden = CurrentInventory websiteVisibility === 'HIDDEN' (when available)
const hiddenCountSafe = computed(() => {
  if (typeof currentInventoryDelisted.value === 'number') return currentInventoryDelisted.value
  const d = websiteVisibilityBreakdown.value.hidden
  return typeof d === 'number' ? d : 0
})
const hiddenCountDisplay = computed(() => {
  if (typeof currentInventoryDelisted.value === 'number') {
    return currentInventoryDelisted.value > 0 ? currentInventoryDelisted.value : null
  }
  const d = websiteVisibilityBreakdown.value.hidden
  // Hide "Hidden" metric when 0 to reduce noise on the dashboard.
  return typeof d === 'number' && d > 0 ? d : null
})

// Dealer-hidden = vehicles with websiteVisibility === 'HIDDEN' (intentionally hidden by dealer)
const dealerHiddenCount = computed(() => {
  const v = data.value as any
  const c = v?.dealerHiddenCount
  return typeof c === 'number' ? c : null
})
const dealerHiddenCountDisplay = computed(() => {
  const c = dealerHiddenCount.value
  return typeof c === 'number' && c > 0 ? c : null
})

// Drafts = manualDraftAdds
const draftCountSafe = computed(() => {
  const d = manualDraftAdds.value
  return typeof d === 'number' ? d : 0
})
const draftCountDisplay = computed(() => {
  const d = manualDraftAdds.value
  return typeof d === 'number' && d > 0 ? d : null
})

// Not on website = hiddenFromWebsite.
const notOnWebsiteCount = computed(() => {
  if (typeof currentInventoryDelisted.value === 'number') return currentInventoryDelisted.value
  const total = stats.value.total
  const on = onWebsiteCount.value
  if (total == null || on == null) return null
  const diff = total - on
  return diff >= 0 ? diff : null
})

// Percent helpers
const onWebsitePct = computed(() => {
  const total = stats.value.total ?? 0
  const on = onWebsiteCount.value
  if (!total || on == null) return null
  return Math.max(0, Math.min(100, Math.round((on / total) * 100)))
})

// Show segmented bar only when buckets are disjoint enough to be truthful.
const segmentsBarReady = computed(() => {
  const total = stats.value.total
  const on = onWebsiteCount.value
  if (total == null || on == null || total <= 0) return false
  const hidden = hiddenCountSafe.value
  const drafts = draftCountSafe.value
  return on + hidden + drafts <= total
})

const onWebsiteBarPct = computed(() => {
  const total = stats.value.total ?? 0
  const on = onWebsiteCount.value ?? 0
  if (!total) return 0
  return Math.round((on / total) * 1000) / 10
})

const hiddenBarPct = computed(() => {
  const total = stats.value.total ?? 0
  const hidden = hiddenCountSafe.value
  if (!total) return 0
  return Math.round((hidden / total) * 1000) / 10
})

const draftBarPct = computed(() => {
  const total = stats.value.total ?? 0
  const drafts = draftCountSafe.value
  if (!total) return 0
  return Math.round((drafts / total) * 1000) / 10
})

// Gentle hint if numbers don’t reconcile (trust-building)
const reconcileHint = computed(() => {
  const total = stats.value.total
  const on = onWebsiteCount.value
  if (total == null || on == null) return ''

  const hidden = hiddenCountDisplay.value
  const drafts = draftCountDisplay.value
  if (hidden == null && drafts == null) return ''

  const hiddenVal = hidden ?? 0
  const draftsVal = drafts ?? 0

  if (on + hiddenVal + draftsVal !== total) {
    return 'Note: counts may not reconcile if “On website” (search index) and “Hidden/Drafts” (inventory snapshot) are not updated at the same time.'
  }
  return ''
})

async function loadPublishHistory() {
  historyError.value = ''
  if (!accountStore.accountId) return
  try {
    isLoadingHistory.value = true
    const jobs = await getAllProcessingJobs(endpoint, accountStore.accountId)
    jobs.sort(
      (a: any, b: any) =>
        new Date(b?.updatedAt || b?.createdAt || 0).getTime() -
        new Date(a?.updatedAt || a?.createdAt || 0).getTime(),
    )
    publishJobs.value = jobs
  } catch (e) {
    historyError.value = e instanceof Error ? e.message : String(e)
    publishJobs.value = []
  } finally {
    isLoadingHistory.value = false
  }
}

async function openArtifacts(row: any) {
  artifactsError.value = ''
  artifactsJson.value = ''
  artifactsObj.value = null
  showArtifacts.value = true
  try {
    isLoadingArtifacts.value = true
    const jazelId = row?.jazelId || accountStore.accountId
    const res = await getPublishArtifacts(endpoint, jazelId, row.correlationId)
    artifactsObj.value = res.artifacts
    artifactsJson.value = JSON.stringify(res.artifacts, null, 2)
  } catch (e) {
    artifactsError.value = e instanceof Error ? e.message : String(e)
  } finally {
    isLoadingArtifacts.value = false
  }
}

async function downloadPublishFile(row: any) {
  try {
    const jazelId = row?.jazelId || accountStore.accountId
    const key = row?.fileKey || row?._job?.fileKey || row?._job?.result?.fileKey
    if (!jazelId || !key) return

    downloadingFileCorrelationId.value = String(row?.correlationId || '')

    const qs = new URLSearchParams({
      jazelId: String(jazelId),
      key: String(key),
      expiresIn: '900',
    })
    const res = await fetch(`${endpoint}/reports/import-feed-file-presign?${qs.toString()}`, {
      method: 'GET',
      headers: accountStore.sessionToken
        ? { Authorization: `Bearer ${accountStore.sessionToken}` }
        : undefined,
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(text || `HTTP ${res.status} ${res.statusText}`)
    }
    const data = await res.json()
    const url = data?.presignedUrl
    if (typeof url !== 'string' || !url) throw new Error('Unable to generate download link')
    window.open(url, '_blank', 'noopener')
  } catch (e) {
    console.error('Failed to download publish file', e)
    alert(e instanceof Error ? e.message : String(e))
  } finally {
    downloadingFileCorrelationId.value = ''
  }
}

function unwrapJamsConfig(raw: any): any {
  if (!raw || typeof raw !== 'object') return raw
  return (raw as any).config ?? (raw as any).Config ?? raw
}

function toPrettyJson(value: any): string {
  try {
    if (value === undefined) return ''
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function countListingRuleNodes(node: any): number {
  if (!node || typeof node !== 'object') return 0
  let count = 1
  const rules = (node as any).rules
  if (Array.isArray(rules)) {
    for (const r of rules) count += countListingRuleNodes(r)
  }
  return count
}

function countBlockRules(node: any): number {
  if (!node || typeof node !== 'object') return 0
  let count = 0
  const action = String((node as any).action || '').toLowerCase()
  if (action === 'block') count += 1
  const rules = (node as any).rules
  if (Array.isArray(rules)) {
    for (const r of rules) count += countBlockRules(r)
  }
  return count
}

function collectRuleReasons(node: any, out: string[], limit = 5) {
  if (!node || typeof node !== 'object' || out.length >= limit) return
  const action = String((node as any).action || '').toLowerCase()
  if (action === 'block') {
    const reason = String((node as any).reason || (node as any).field || '').trim()
    if (reason && !out.includes(reason)) out.push(reason)
  }
  const rules = (node as any).rules
  if (Array.isArray(rules)) {
    for (const r of rules) collectRuleReasons(r, out, limit)
  }
}

const artifactsConfigs = computed(() => {
  const a = artifactsObj.value as any
  const configsRaw = a?.configs
  const configs = configsRaw && typeof configsRaw === 'object' ? configsRaw : {}

  const listingRulesRaw = unwrapJamsConfig(configs.listingRules)
  const listingRulesCfg = listingRulesRaw && typeof listingRulesRaw === 'object' ? listingRulesRaw : null
  const ruleSet =
    listingRulesCfg?.all?.inventory_membership_rules?.ruleset ??
    listingRulesCfg?.inventory_membership_rules?.ruleset ??
    listingRulesCfg?.ruleset ??
    null

  const logic = ruleSet?.logic ? String(ruleSet.logic) : null
  const nodeCount = ruleSet ? countListingRuleNodes(ruleSet) : 0
  const blockRuleCount = ruleSet ? countBlockRules(ruleSet) : 0
  const reasons: string[] = []
  if (ruleSet) collectRuleReasons(ruleSet, reasons, 5)
  const reasonsHuman = reasons.map(humanizeReason).filter(Boolean)

  const enabled =
    typeof listingRulesCfg?.Enable === 'boolean'
      ? listingRulesCfg.Enable
      : typeof listingRulesCfg?.enable === 'boolean'
        ? listingRulesCfg.enable
        : null

  const fieldMappingRaw = unwrapJamsConfig(configs.fieldMapping)
  const fieldMappingCfg = fieldMappingRaw && typeof fieldMappingRaw === 'object' ? fieldMappingRaw : null
  const mappingConfig = fieldMappingCfg?.mapping_config && typeof fieldMappingCfg.mapping_config === 'object'
    ? fieldMappingCfg.mapping_config
    : null
  const segmentKeys = mappingConfig ? Object.keys(mappingConfig) : []
  let mappingCount = 0
  const sources = new Set<string>()
  const sampleFields: string[] = []
  if (mappingConfig) {
    for (const [, seg] of Object.entries<any>(mappingConfig)) {
      const mappings = seg?.field_mapping?.mappings
      if (Array.isArray(mappings)) {
        mappingCount += mappings.length
        for (const m of mappings) {
          const outField = getMappingOutputField(m)
          if (outField && sampleFields.length < 8 && !sampleFields.includes(outField)) {
            sampleFields.push(outField)
          }
          const srcs = Array.isArray(m?.sources) ? m.sources : []
          for (const s of srcs) sources.add(humanizeSourceId(s))
        }
      }
    }
  }

  function compactLabel(values: string[], limit = 3): string {
    const unique = Array.from(new Set(values.map((v) => String(v || '').trim()).filter(Boolean)))
    if (unique.length === 0) return '—'
    if (unique.length <= limit) return unique.join(', ')
    return `${unique.slice(0, limit).join(', ')} +${unique.length - limit}`
  }

  const segmentationRaw = unwrapJamsConfig(configs.segmentsOrGrouping)
  const segmentationCfg = segmentationRaw && typeof segmentationRaw === 'object' ? segmentationRaw : null
  const segmentsObj =
    segmentationCfg?.segments && typeof segmentationCfg.segments === 'object'
      ? segmentationCfg.segments
      : segmentationCfg && typeof segmentationCfg === 'object'
        ? segmentationCfg
        : null
  const groupingSegmentIds = segmentsObj
    ? Object.keys(segmentsObj).filter((k) => !['Enable', 'isActive', 'lastUpdated', 'version'].includes(k))
    : []

  return {
    listingRules: listingRulesCfg
      ? {
          enabled,
          logic,
          nodeCount,
          blockRuleCount,
          sampleReasons: reasonsHuman,
          _raw: listingRulesCfg,
          _ruleset: ruleSet,
        }
      : null,
    fieldMapping: fieldMappingCfg
      ? {
          segmentCount: segmentKeys.length,
          mappingCount,
          sourceCount: sources.size || null,
          sampleFields,
          segmentLabel: compactLabel(segmentKeys, 3),
          sourceLabel: compactLabel(Array.from(sources), 3),
          _raw: fieldMappingCfg,
          _mappingConfig: mappingConfig,
        }
      : null,
    segmentsOrGrouping: segmentationCfg
      ? {
          segmentNames: groupingSegmentIds,
          segmentLabel: groupingSegmentIds.length ? groupingSegmentIds.join(', ') : '—',
          _raw: segmentationCfg,
          _segments: segmentsObj,
        }
      : null,
  }
})

function isObject(v: any): v is Record<string, any> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}

function humanizeReason(raw: unknown): string {
  const r = String(raw || '').trim()
  if (!r) return ''
  switch (r.toUpperCase()) {
    case 'VIN_INVALID_OR_MISSING':
      return 'VIN is missing or invalid'
    case 'REQUIRED_FIELD_MISSING':
      return 'Required info is missing'
    case 'VIN_MANUALLY_BLOCKED':
      return 'Manually hidden'
    case 'RULE_MATCH':
      return 'Matched a rule'
    default:
      // Make CONSTANT_LIKE strings a bit friendlier.
      return r
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
  }
}

function humanizeFieldPath(raw: unknown): string {
  const s = String(raw || '').trim()
  if (!s) return ''
  // Drop feed prefix like "vauto.VIN" -> "VIN"
  const last = s.includes('.') ? s.split('.').slice(-1)[0] : s
  // Common fields
  const k = last.toLowerCase()
  if (k === 'vin') return 'VIN'
  if (k === 'make') return 'Make'
  if (k === 'model') return 'Model'
  if (k === 'year') return 'Year'
  // Snake_case -> Title Case
  return last
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function humanizeOperator(opRaw: unknown): string {
  const op = String(opRaw || '').trim().toLowerCase()
  switch (op) {
    case 'missing':
      return 'is missing'
    case 'equals_ci':
      return 'is'
    case 'equals':
      return 'is'
    case 'length_not_in':
      return 'length is not'
    case 'not_in':
      return 'is not one of'
    case 'in':
      return 'is one of'
    case 'contains':
      return 'contains'
    case 'not_contains':
      return 'does not contain'
    case 'starts_with':
      return 'starts with'
    case 'ends_with':
      return 'ends with'
    default:
      return op.replace(/_/g, ' ')
  }
}

function humanizeValue(value: any): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map((v) => JSON.stringify(v)).join(', ')
  return JSON.stringify(value)
}

function humanizeSourceId(raw: unknown): string {
  const s = String(raw || '').trim()
  if (!s) return ''
  const lower = s.toLowerCase()
  if (lower === 'chrome_cvd_1_0') return 'chrome'
  if (lower.startsWith('import_')) return lower.slice('import_'.length)
  return lower
}

function getMappingOutputField(mapping: any): string {
  const candidates = [mapping?.field, mapping?.output_field, mapping?.outputField, mapping?.output, mapping?.to]
  for (const c of candidates) {
    const s = String(c || '').trim()
    if (s) return s
  }
  return ''
}

function formatRuleCondition(rule: any): string {
  if (!isObject(rule)) return 'unknown rule'
  const fieldRaw = rule.field || rule.field_name || ''
  const field = humanizeFieldPath(fieldRaw)
  const opRaw = rule.operator || rule.op || ''
  const op = String(opRaw || '').trim().toLowerCase()
  const opHuman = humanizeOperator(opRaw)
  const value = rule.value ?? rule.values ?? rule.expected ?? rule.match ?? null

  // Special-case: "missing true" -> "Field is missing"
  if (op === 'missing') {
    return `${field} is missing`
  }

  // Special-case: VIN length_not_in [17] -> "VIN is not 17 characters long"
  if (field.toUpperCase() === 'VIN' && op === 'length_not_in' && Array.isArray(value) && value.length === 1) {
    const n = value[0]
    return `VIN is not ${n} characters long`
  }

  // equals_ci / equals: drop the technical suffix
  if ((op === 'equals_ci' || op === 'equals') && typeof value === 'string' && value) {
    return `${field} is “${value}”`
  }

  // General case
  const valueHuman = humanizeValue(value)
  if (op === 'length_not_in' && valueHuman) {
    return `${field} ${opHuman} ${valueHuman} characters`
  }
  if (valueHuman) return `${field} ${opHuman} ${valueHuman}`
  return `${field} ${opHuman}`.trim()
}

type ListingRuleUiItem =
  | { kind: 'group'; depth: number; logic: string | null; childrenCount: number }
  | { kind: 'rule'; depth: number; condition: string; action: string | null; reason: string | null }

function flattenRulesetToItems(node: any, opts?: { maxItems?: number }): ListingRuleUiItem[] {
  const maxItems = opts?.maxItems ?? 120
  const items: ListingRuleUiItem[] = []

  const walk = (n: any, depth: number) => {
    if (items.length >= maxItems) return
    if (!isObject(n)) {
      items.push({
        kind: 'rule',
        depth,
        condition: String(n),
        action: null,
        reason: null,
      })
      return
    }

    const type = String(n.type || '').toLowerCase()
    const logic = n.logic ? String(n.logic).toUpperCase() : null
    const rules = Array.isArray((n as any).rules) ? (n as any).rules : null

    // Group node
    if (type === 'group' || rules) {
      const children = rules || []
      items.push({
        kind: 'group',
        depth,
        logic,
        childrenCount: children.length,
      })
      for (const r of children) walk(r, depth + 1)
      return
    }

    // Leaf rule node
    const action = String(n.action || '').trim()
    const reason = humanizeReason(n.reason || '')
    items.push({
      kind: 'rule',
      depth,
      condition: formatRuleCondition(n),
      action: action ? action.toUpperCase() : null,
      reason: reason || null,
    })
  }

  walk(node, 0)
  if (items.length >= maxItems) {
    items.push({
      kind: 'rule',
      depth: 0,
      condition: `… truncated (showing first ${maxItems} items)`,
      action: null,
      reason: null,
    })
  }
  // UX: The root ruleset is usually just a wrapper (e.g., AND group). Hide that top-level wrapper
  // so the first visible line is meaningful (e.g., "Any of these …").
  if (items.length > 0 && items[0].kind === 'group' && items[0].depth === 0) {
    const [, ...rest] = items
    return rest.map((it) => ({ ...it, depth: Math.max(0, (it as any).depth - 1) })) as ListingRuleUiItem[]
  }
  return items
}

type SegmentRuleUiItem =
  | { kind: 'group'; depth: number; logic: string | null }
  | { kind: 'rule'; depth: number; condition: string }

function flattenRulesetToSegmentItems(node: any, opts?: { maxItems?: number }): SegmentRuleUiItem[] {
  const maxItems = opts?.maxItems ?? 120
  const items: SegmentRuleUiItem[] = []

  const walk = (n: any, depth: number) => {
    if (items.length >= maxItems) return
    if (!isObject(n)) {
      items.push({ kind: 'rule', depth, condition: String(n) })
      return
    }
    const logic = n.logic ? String(n.logic).toUpperCase() : null
    const rules = Array.isArray((n as any).rules) ? (n as any).rules : null
    if (rules) {
      items.push({ kind: 'group', depth, logic })
      for (const r of rules) walk(r, depth + 1)
      return
    }
    items.push({ kind: 'rule', depth, condition: formatRuleCondition(n) })
  }

  walk(node, 0)
  if (items.length >= maxItems) {
    items.push({ kind: 'rule', depth: 0, condition: `… truncated (showing first ${maxItems} items)` })
  }
  // Hide root wrapper group
  if (items.length > 0 && items[0].kind === 'group' && items[0].depth === 0) {
    const [, ...rest] = items
    return rest.map((it) => ({ ...it, depth: Math.max(0, (it as any).depth - 1) })) as SegmentRuleUiItem[]
  }
  return items
}

const artifactsConfigDetailsFriendly = computed(() => {
  const listing = artifactsConfigs.value.listingRules
  const mapping = artifactsConfigs.value.fieldMapping
  const seg = artifactsConfigs.value.segmentsOrGrouping

  // Publish rules: show a readable walk of the ruleset tree
  const listingLines: string[] = []
  const listingItems: ListingRuleUiItem[] = []
  if (listing?._ruleset) {
    listingLines.push('These are the checks that can hide a vehicle from the site.')
    listingItems.push(...flattenRulesetToItems(listing._ruleset, { maxItems: 120 }))
  }

  // Field mapping: keep summary minimal (segment + sources) + full field list
  const mappingSummaryLines: string[] = []
  const mappingRows: Array<{ segment: string; field: string; source: string; _search: string }> = []
  if (mapping?._mappingConfig && isObject(mapping._mappingConfig)) {
    const segmentNames = Object.keys(mapping._mappingConfig)
    // summary intentionally omitted (chips above show segment/source)

    const sources = new Set<string>()

    for (const [, segCfg] of Object.entries<any>(mapping._mappingConfig)) {
      const mappings = segCfg?.field_mapping?.mappings
      if (!Array.isArray(mappings)) continue
      for (const m of mappings) {
        const out = getMappingOutputField(m)
        const srcs = Array.isArray(m?.sources) ? m.sources : []
        for (const s of srcs) sources.add(humanizeSourceId(s))
      }
    }

    // summary intentionally omitted (chips above show segment/source)

    // Build a flat list: Segment + Field + Source (one row per source).
    const seen = new Set<string>()
    for (const [segmentName, segCfg] of Object.entries<any>(mapping._mappingConfig)) {
      const mappings = segCfg?.field_mapping?.mappings
      if (!Array.isArray(mappings)) continue

      for (const m of mappings) {
        const out = getMappingOutputField(m)
        if (!out) continue
        const srcs: unknown[] = Array.isArray(m?.sources) ? (m.sources as unknown[]) : []
        const sourcesList: string[] = Array.from(
          new Set(srcs.map((s) => humanizeSourceId(s)).filter((s): s is string => !!s)),
        )

        const sourcesToRender = sourcesList.length > 0 ? sourcesList : ['unknown']
        for (const source of sourcesToRender) {
          const key = `${segmentName}|${out}|${source}`
          if (seen.has(key)) continue
          seen.add(key)
          mappingRows.push({
            segment: String(segmentName),
            field: out,
            source,
            _search: `${segmentName} ${out} ${source}`.toLowerCase(),
          })
        }
      }
    }
  }

  // Grouping: show segments and their rulesets
  const segmentsDetails: Array<{ segmentId: string; segmentName: string; items: SegmentRuleUiItem[] }> = []
  if (seg?._segments && isObject(seg._segments)) {
    const keys = Object.keys(seg._segments).filter((k) => !['Enable', 'isActive', 'lastUpdated', 'version'].includes(k))
    for (const segmentId of keys) {
      const segDef = (seg._segments as any)[segmentId]
      const segmentName = String(segDef?.name || segmentId)
      const ruleset = segDef?.ruleset
      const items = ruleset ? flattenRulesetToSegmentItems(ruleset, { maxItems: 120 }) : []
      segmentsDetails.push({ segmentId, segmentName, items })
    }
  }

  return {
    listingRules: listingLines.length || listingItems.length ? { lines: listingLines, items: listingItems } : null,
    fieldMapping: mappingSummaryLines.length || mappingRows.length
      ? { summaryLines: mappingSummaryLines, rows: mappingRows.sort((a, b) => (a.segment + a.field + a.source).localeCompare(b.segment + b.field + b.source)) }
      : null,
    segmentsOrGrouping: segmentsDetails.length ? { segments: segmentsDetails } : null,
  }
})

async function openLogs(row: any) {
  logsError.value = ''
  logsJson.value = ''
  humanReadableLogs.value = []
  showLogs.value = true
  try {
    isLoadingLogs.value = true
    const jazelId = row?.jazelId || accountStore.accountId
    const res = await getPublishLogs(endpoint, jazelId, row.correlationId, { maxLines: 500 })
    logsJson.value = JSON.stringify(res.entries || [], null, 2)
    
    // Parse logs into human-readable format
    const parsed = parsePublishLogsToHumanReadable(res.entries || [])
    humanReadableLogs.value = parsed.logs
  } catch (e) {
    logsError.value = e instanceof Error ? e.message : String(e)
  } finally {
    isLoadingLogs.value = false
  }
}

watch(showHistory, (open) => {
  if (open) loadPublishHistory()
})

const processingDetails = ref<any[]>([])

const initData = async (url: string, accountId: string): Promise<any> => {
  // 1) Activity: unique VINs per status (Today / Week / Month) from VehicleStatusHistory
  // Runs today still comes from the job tracker for the "N runs" label.
  const allJobs = await getAllProcessingJobs(url, accountId)
  publishJobs.value = [...(allJobs || [])].sort(
    (a: any, b: any) =>
      new Date(b?.updatedAt || b?.createdAt || 0).getTime() -
      new Date(a?.updatedAt || a?.createdAt || 0).getTime(),
  )

  const nowJobs = new Date()
  const startOfDayJobs = new Date(nowJobs.getFullYear(), nowJobs.getMonth(), nowJobs.getDate()).getTime()
  const endOfDayJobs = new Date(nowJobs.getFullYear(), nowJobs.getMonth(), nowJobs.getDate() + 1).getTime()

  const todayJobs = (allJobs || []).filter((job: any) => {
    const timestamp = job.updatedAt || job.createdAt
    if (!timestamp) return false
    const ts = new Date(timestamp).getTime()
    return ts >= startOfDayJobs && ts < endOfDayJobs && job.status === 'SUCCEEDED'
  })
  runsToday.value = todayJobs.length

  const latestSucceededJob = (allJobs || [])
    .filter((job: any) => job?.status === 'SUCCEEDED' && job?.correlationId)
    .sort((a: any, b: any) => {
      const aTs = new Date(a.finishedAt || a.updatedAt || a.createdAt || 0).getTime()
      const bTs = new Date(b.finishedAt || b.updatedAt || b.createdAt || 0).getTime()
      return bTs - aTs
    })[0]

  const latestJobCorrelationId = String(latestSucceededJob?.correlationId || '').trim()

  // Feed Guardrail: find the most-recent FLAGGED_LARGE_DELTA job (if any).
  // Only show if no more-recent SUCCEEDED job exists (i.e. the flag is still unresolved).
  const mostRecentFlagged = (allJobs || [])
    .filter((job: any) => job?.status === 'FLAGGED_LARGE_DELTA')
    .sort((a: any, b: any) => {
      const aTs = new Date(a.createdAt || 0).getTime()
      const bTs = new Date(b.createdAt || 0).getTime()
      return bTs - aTs
    })[0] ?? null
  const latestSucceededTs = latestSucceededJob
    ? new Date(latestSucceededJob.finishedAt || latestSucceededJob.updatedAt || latestSucceededJob.createdAt || 0).getTime()
    : 0
  const flaggedTs = mostRecentFlagged
    ? new Date(mostRecentFlagged.createdAt || 0).getTime()
    : 0
  flaggedJob.value = mostRecentFlagged && flaggedTs >= latestSucceededTs ? mostRecentFlagged : null

  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfWeek = new Date(startOfDay)
  // week starts on Sunday to match JS Date; adjust if you prefer Monday
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)

  const [today, week, month] = await Promise.all([
    getInventoryActivityCounts({
      endpoint: url,
      accountId,
      startIso: startOfDay.toISOString(),
      endIso: end.toISOString(),
    }),
    getInventoryActivityCounts({
      endpoint: url,
      accountId,
      startIso: startOfWeek.toISOString(),
      endIso: end.toISOString(),
    }),
    getInventoryActivityCounts({
      endpoint: url,
      accountId,
      startIso: startOfMonth.toISOString(),
      endIso: end.toISOString(),
    }),
  ])

  todayAdded.value = today?.added ?? 0
  todayRemoved.value = today?.removed ?? 0
  weekAdded.value = week?.added ?? 0
  weekRemoved.value = week?.removed ?? 0
  monthAdded.value = month?.added ?? 0
  monthRemoved.value = month?.removed ?? 0

  // 2) ISE Last Processed: use import-feed-processing-details (latest per source)
  let details: any[] = []
  try {
    const res = await getSourcesProcessDetails(url, accountId)
    details = Array.isArray(res) ? res : []
  } catch (e) {
    console.error('[Dashboard] getSourcesProcessDetails failed:', e)
    details = []
  }
  processingDetails.value = details
  console.log('Processing Details:', details)

  if (details && details.length > 0) {
    hasProcessedData.value = true

    // Find the most recent processed time across all sources (and capture its correlationId)
    let latestProcessedTime = ''
    let latestCorrelationId = ''

    // Aggregate membership delta counts from latest processing snapshot (all sources)
    let snapshotAdded = 0
    let snapshotRemoved = 0

    details.forEach((detail: any) => {
      if (detail.lastProcessedTime && detail.lastProcessedTime > latestProcessedTime) {
        latestProcessedTime = detail.lastProcessedTime
        latestCorrelationId =
          String(
            detail?.correlationId ||
              detail?.reportData?.correlationId ||
              detail?.reportData?.metadata?.correlationId ||
              '',
          ).trim()
      }
      if (detail.reportData?.counts) {
        snapshotAdded += detail.reportData.counts.added || 0
        snapshotRemoved += detail.reportData.counts.removed || 0
      }
    })

    // Update ISE snapshot for the "ISE Last Processed" card
    if (!latestCorrelationId && latestJobCorrelationId) {
      latestCorrelationId = latestJobCorrelationId
    }

    const jobForLatestCorrelation =
      (allJobs || []).find((j: any) => String(j?.correlationId || '').trim() === latestCorrelationId) ||
      latestSucceededJob ||
      null
    const { triggerType, triggeredBy } = resolvePublishTrigger(jobForLatestCorrelation)

    ise.value = {
      lastRunRaw: latestProcessedTime || null,
      lastRun: latestProcessedTime ? formatDateTimeWithTzShort(latestProcessedTime) : 'N/A',
      correlationId: latestCorrelationId,
      added: snapshotAdded,
      removed: snapshotRemoved,
      triggerType,
      triggeredBy,
    }

    // 2b) Publish-run metrics (from VehicleStatusHistory by correlationId) for FEED/MANUAL breakdown.
    await loadPublishRunMetricsForCorrelation(url, accountId, latestCorrelationId)

    // Collect all VINs from latest processing details (for "View Latest Changes")
    const allAdded: string[] = []
    const allChanged: string[] = []
    const allRemoved: string[] = []

    details.forEach((detail: any) => {
      if (detail.reportData) {
        allAdded.push(...(detail.reportData.added || []))
        allChanged.push(...(detail.reportData.changed || []))
        allRemoved.push(...(detail.reportData.removed || []))
      }
    })

    // Update latestChanges ref (for dialogs)
    latestChanges.value = {
      added: allAdded,
      updated: allChanged.map((vin) => ({
        vin,
        changes: 'Vehicle data updated',
      })),
      removed: allRemoved,
    }

    // Notify publish store to reconcile published state
    if (latestProcessedTime) {
      publishStore.reconcilePublished(latestProcessedTime)
    }

    // Simple alert heuristics
    const alerts: Array<{ id: string; text: string }> = []
    const missingPhotos = 0 // future: compute from stats/items
    const missingPrice = 0
    if (missingPhotos > 0)
      alerts.push({ id: 'photos', text: `${missingPhotos} vehicles missing photos` })
    if (missingPrice > 0)
      alerts.push({ id: 'price', text: `${missingPrice} vehicles missing price` })
    const delayed = sourcesUsed.value.some((s) => s.status === 'Delayed')
    if (delayed) alerts.push({ id: 'delayed', text: 'Manual feed is 2h late' })
    openAlerts.value = alerts

    await loadCurrentInventoryDashboardStats(url, accountId)
  } else {
    hasProcessedData.value = false

    if (latestSucceededJob) {
      const latestRunRaw = publishJobTimestamp(latestSucceededJob)
      const latestCorrelationId = String(latestSucceededJob.correlationId || '').trim()
      const { triggerType, triggeredBy } = resolvePublishTrigger(latestSucceededJob)

      // Manual-only dealers have publish jobs but no feed processing details. Treat the latest
      // successful publish job as the dashboard's latest update and drive metrics from
      // VehicleStatusHistory by correlation ID.
      ise.value = {
        lastRunRaw: latestRunRaw || null,
        lastRun: latestRunRaw ? formatDateTimeWithTzShort(latestRunRaw) : 'N/A',
        correlationId: latestCorrelationId,
        added: 0,
        removed: 0,
        triggerType,
        triggeredBy,
      }
      await loadPublishRunMetricsForCorrelation(url, accountId, latestCorrelationId)
      latestChanges.value = { added: [], updated: [], removed: [] }
      if (latestRunRaw) {
        publishStore.reconcilePublished(latestRunRaw)
      }
    } else {
      // No processing data or successful publish history available yet.
      ise.value = {
        lastRunRaw: null,
        lastRun: 'N/A',
        correlationId: '',
        added: 0,
        removed: 0,
        triggerType: '',
        triggeredBy: '',
      }
      resetPublishRunMetrics()
      latestChanges.value = { added: [], updated: [], removed: [] }
    }

    openAlerts.value = []
    await loadCurrentInventoryDashboardStats(url, accountId)
  }
}

onMounted(async () => {
  await loadLatestInventoryUpdate()
  // Load segments so we can show custom segment summary (if any)
  dashboardSegmentsReady.value = false
  try {
    await segmentStore.getSegments({ includeDraft: false })
  } finally {
    dashboardSegmentsReady.value = true
  }
})

// Merge cached dealer sources with processing details to show per-source panel
const sourcesUsed = computed(() => {
  const available = (sourcesStore.sources || []).filter((s) => s.type === 'feed')
  const byId = new Map<string, any>()
  for (const d of processingDetails.value) {
    if (d?.importProductId) byId.set(d.importProductId, d)
  }

  const now = Date.now()

  return available.map((s) => {
    const d = byId.get(s.id) || {}
    const counts = d?.reportData?.counts || { added: 0, changed: 0, removed: 0 }
    const hasReport = !!d?.reportData
    const reportKind = d?.reportData?.reportKind ?? null
    const feedRole = d?.reportData?.feedRole ?? null
    const isSupplementary = feedRole === 'supplementary' || reportKind === 'feed_intake'
    const feedRoleLabel = hasReport ? (isSupplementary ? 'Supplementary' : 'Primary') : null
    const displayCount =
      d?.reportData?.counts?.feedVinCount ??
      d?.reportData?.counts?.total ??
      0
    const last = d?.lastProcessedTime ? new Date(d.lastProcessedTime) : null
    const minutes = last ? Math.floor((now - last.getTime()) / 60000) : null
    const delayed = minutes != null && minutes > 120 // >2h considered delayed

    return {
      id: s.id,
      label: d?.reportData?.feedSource || s.id,
      short: (s.friendlyName || s.id).slice(0, 2).toUpperCase(),
      type: s.type,
      lastProcessedTimeRaw: d?.lastProcessedTime || null,
      lastProcessedTime: last ? formatTimeWithTzShort(last) : null,
      counts,
      reportKind,
      feedRole,
      isSupplementary,
      hasReport,
      feedRoleLabel,
      displayCount,
      hasData:
        !!d?.latestReportUrl ||
        counts.added + counts.changed + counts.removed > 0 ||
        displayCount > 0,
      status: last ? (delayed ? 'Delayed' : 'OK') : 'N/A',
      statusColor: last ? (delayed ? 'orange' : 'green') : 'grey',
    }
  })
})

// Segment summary boxes (Trucks, SUV, Sedan, EV/Hybrid if present)
const customSegments = computed(() => {
  const isCore = (id: unknown) => {
    const key = String(id || '').trim().toLowerCase()
    return key === 'all' || key === 'new' || key === 'used' || key === 'cpo'
  }

  return (segmentStore.segments || []).filter((s: any) => {
    const id = (s as any)?.id
    // Prefer explicit flag when available, but fall back to "non-core id" heuristic
    // so custom segments still appear even if backend doesn't include `custom: true`.
    if ((s as any)?.custom === true) return true
    return !!id && !isCore(id)
  })
})
const segmentSummary = computed(() => {
  if (!customSegments.value || customSegments.value.length === 0) return []
  const counts = (data.value?.segments || []) as Array<{ segmentId: string; count: number }>
  const byId = (id: string) => counts.find((c) => c.segmentId === id)?.count ?? 0
  return customSegments.value
    .slice(0, 5)
    .map((seg: any) => ({ id: seg.id, label: seg.name, count: byId(seg.id) }))
})

// Download feed raw file for a given source
const downloadSource = async (importProductId: string) => {
  try {
    const stat = await getSourceStats(endpoint, accountStore.accountId, importProductId)
    if (!stat?.presignedUrl) return
    const a = document.createElement('a')
    a.href = stat.presignedUrl
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (e) {
    console.error('downloadSource error', e)
  }
}

async function loadLatestInventoryUpdate() {
  latestInventoryUpdateState.value = 'loading'
  latestInventoryUpdateError.value = ''
  try {
    // Ensure sources cache is ready (Dashboard depends on it for processing details).
    if (!sourcesStore.sources?.length && !sourcesStore.isLoading) {
      await sourcesStore.fetchSources(endpoint, accountStore.accountId)
    }

    await initData(endpoint, accountStore.accountId)
    latestInventoryUpdateState.value = 'loaded'
  } catch (e) {
    latestInventoryUpdateError.value = e instanceof Error ? e.message : String(e)
    latestInventoryUpdateState.value = 'error'
  }
}

const goToDebug = async () => {
    console.log("Navigating to debug dashboard...");
    try {
        await router.push({ name: 'DebugDashboard' });
    } catch (e) {
        console.error("Navigation failed:", e);
    }
}

// ── Feed Guardrail override actions ──────────────────────────────────────────

async function handleGuardrailOverride(action: 'approve' | 'discard') {
  const job = flaggedJob.value
  if (!job?.correlationId) return

  guardrailOverrideLoading.value = action
  guardrailOverrideError.value = ''

  try {
    const res = await fetch(`${endpoint}/override-feed-guardrail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accountStore.sessionToken}`,
      },
      body: JSON.stringify({ correlationId: job.correlationId, action }),
    })

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}))
      throw new Error(errBody?.error || `HTTP ${res.status}`)
    }

    // Clear banner + refresh jobs after a short delay to allow status propagation.
    flaggedJob.value = null
    await new Promise((r) => setTimeout(r, 1500))
    await loadLatestInventoryUpdate()
  } catch (e) {
    guardrailOverrideError.value = e instanceof Error ? e.message : String(e)
  } finally {
    guardrailOverrideLoading.value = null
  }
}

function viewFlaggedFeedLogs() {
  const job = flaggedJob.value
  if (!job?.correlationId) return
  openLogs(job)
}

// ─────────────────────────────────────────────────────────────────────────────
</script>

<style scoped>
.dash-card {
  border: 1px solid #e5e7eb; /* simple light border */
  box-shadow: none !important; /* ensure elevation=0 */
  transition:
    box-shadow 0.2s ease,
    transform 0.02s ease;
}
.dash-card:hover {
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.08) !important;
}
.source-card {
  border: 1px solid #e5e7eb; /* simple light border */
  transition:
    box-shadow 0.2s ease,
    transform 0.02s ease;
}

.publish-section-card {
  border: 1px solid #e5e7eb;
}

.ise-metrics-grid {
  display: grid;
  gap: 14px;
}

/* Keep desktop in a single row (no wrapping). */
@media (min-width: 960px) {
  .ise-metrics-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

/* Smaller screens can wrap naturally for readability. */
@media (max-width: 959px) {
  .ise-metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.metric-box {
  padding: 16px;
  border-radius: 14px;
  text-align: center;
  min-height: 82px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.run-metrics-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 6px;
}

.run-metrics-table th,
.run-metrics-table td {
  text-align: left;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 12px;
}

.run-metrics-table th {
  color: #6b7280;
  font-weight: 600;
}
.metric-added {
  background: #ecfdf5;
}
.metric-updated {
  background: #eef2ff;
}
.metric-removed {
  background: #fff1f2;
}

.debug-pre {
  max-height: 520px;
  overflow: auto;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  font-size: 12px;
  line-height: 1.4;
}

.publish-history-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 12px 14px;
  background: #fff;
  margin: 10px 0;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}
.publish-history-card:hover {
  background: rgba(0, 0, 0, 0.015);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}
.publish-history-file {
  max-width: min(520px, 48vw);
  display: inline-block;
}
.publish-history-actions .v-btn {
  text-transform: none;
}

.sticky-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: min(86vh, 820px);
}
.sticky-dialog-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #eef2f7;
}
.sticky-dialog-body {
  overflow-y: auto;
  padding-top: 12px;
  padding-bottom: 12px;
}
.vin-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  cursor: pointer;
}
.vin-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 4px;
}
.sticky-dialog-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #fff;
  border-top: 1px solid #eef2f7;
}

.publish-history-table :deep(.v-data-table__tr) {
  border-bottom: none !important;
}
.publish-history-table :deep(.v-data-table__td) {
  border-bottom: none !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.publish-history-table :deep(.v-data-table__th) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
.publish-history-table :deep(.v-table__wrapper > table) {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}
.publish-history-table :deep(.v-data-table-footer) {
  border-top: none !important;
}

.publish-logs-source-chip {
  opacity: 0.6;
}
.publish-logs-activity {
  font-weight: 600;
}

/* Segmented bar for the Inventory card */
.segbar {
  height: 8px;
  width: 100%;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  border: 1px solid #e5e7eb;
  background: rgba(0, 0, 0, 0.03);
}
.seg {
  height: 100%;
}
.seg-on {
  background: rgba(34, 197, 94, 0.45);
}
.seg-hidden {
  background: rgba(245, 158, 11, 0.45);
}
.seg-draft {
  background: rgba(59, 130, 246, 0.45);
}

/* Match count colors to the segmented bar colors */
.website-count-hidden {
  color: rgba(245, 158, 11, 1);
}
.website-count-draft {
  color: rgba(59, 130, 246, 1);
}

/* Subtle affordance for clickable dashboard metrics */
.clickable-metric {
  cursor: pointer;
}
.clickable-metric:hover {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
}
.clickable-metric:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.65);
  outline-offset: 2px;
  border-radius: 10px;
}

/* Subtle affordance for clickable chips */
.clickable-chip {
  cursor: pointer;
}
.clickable-chip:hover :deep(.v-chip__content) {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
}
.clickable-chip:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.65);
  outline-offset: 2px;
  border-radius: 999px;
}

/* Feed Guardrail banner */
.feed-guardrail-banner {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgb(var(--v-theme-warning));
}
.guardrail-header {
  background: linear-gradient(135deg, #e65100, #f57c00);
}
</style>
